import { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Calendar, Music, Settings, ChevronLeft, ChevronRight, X, Plus, Droplet, Trash2, Bell, Circle, Download, Check, Heart, Snowflake, Sprout, Sun, Leaf, Star, ExternalLink } from "lucide-react";
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { saveUserCycleToSupabase, loadUserCycleFromSupabase, updateUserCycleInSupabase, supabase, getPartnerLink, createPartnerLink, revokePartnerLink, getPartnerCycleData, checkIsPremium, updateIsPremium, markSeasonsIntroSeen, upsertMood, deleteMood, getMoodsForMonth, checkBloodDropTooltipSeen, markBloodDropTooltipSeen, checkOnboardingCompleted, markOnboardingCompleted, saveEventToSupabase, loadEventsFromSupabase, deleteEventFromSupabase, getEventCountsByDateFromSupabase, getNotificationsEnabled as getNotificationsEnabledSupabase, setNotificationsEnabled as setNotificationsEnabledSupabase, getContraceptiveMode as getContraceptiveModeSupabase, setContraceptiveMode as setContraceptiveModeSupabase } from "./lib/supabase";
import { initRevenueCat, checkPremiumStatus, purchaseMonthly, restorePurchases, isNative } from "./services/revenuecat";

// App startup logging
console.log('🚀 ILIK App starting...')
console.log('Environment check:')
console.log('- REACT_APP_BACKEND_URL:', process.env.REACT_APP_BACKEND_URL ? 'Found' : 'Missing')
console.log('- EXPO_PUBLIC_SUPABASE_URL:', process.env.EXPO_PUBLIC_SUPABASE_URL ? 'Found' : 'Missing')
console.log('- EXPO_PUBLIC_SUPABASE_ANON_KEY:', process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ? 'Found' : 'Missing')

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Spring flower icon URL
const SPRING_FLOWER_URL = "https://customer-assets.emergentagent.com/job_174d8d9c-07b8-4060-a796-8866023eb6e4/artifacts/3gnu48kb_flower.png";

// Phase configurations
const PHASE_CONFIG = {
  Winter: {
    emoji: "❄️",
    color: "#E53935",
    bgColor: "rgba(229, 57, 53, 0.15)",
    tagline: "Low capacity. Focus inward",
    suggestions: ["Rest", "Cancel freely", "Gentle movement"],
    maxEvents: 3,
    capacityMessage: {
      heading: "That's enough for today",
      message: "Your Winter body is working hard enough already. Protect this day. Rest is not empty space. It is the work.",
      button: "I will rest"
    }
  },
  Spring: {
    emoji: "spring-flower",
    color: "#43A047",
    bgColor: "rgba(67, 160, 71, 0.15)",
    tagline: "Rising. Good time to begin",
    suggestions: ["Start something", "Plan ahead", "Connect"],
    maxEvents: 6,
    capacityMessage: {
      heading: "You're fully blooming",
      message: "Your Spring is already full of life. A garden that is overpacked cannot breathe. Leave space for things to grow.",
      button: "Got it"
    }
  },
  Summer: {
    emoji: "☀️",
    color: "#FDD835",
    bgColor: "rgba(253, 216, 53, 0.15)",
    tagline: "At your strongest. Show up fully",
    suggestions: ["Schedule the meeting", "Say yes", "Create"],
    maxEvents: 7,
    capacityMessage: {
      heading: "Your Summer is shining bright",
      message: "You have packed this day beautifully. Even the sun sets. Protect some space for yourself.",
      button: "Ok"
    }
  },
  Autumn: {
    emoji: "🍂",
    color: "#FF6F00",
    bgColor: "rgba(255, 111, 0, 0.15)",
    tagline: "Slowing down. Finish, don't start",
    suggestions: ["Finish things", "Go inward", "Say no"],
    maxEvents: 5,
    capacityMessage: {
      heading: "This is enough",
      message: "Your Autumn capacity is full. You are already doing more than you know. Let this day close gently.",
      button: "I hear you"
    }
  }
};

// Phase colors for calendar tints
const PHASE_COLORS = {
  Winter: "rgba(229, 57, 53, 0.3)",
  Spring: "rgba(67, 160, 71, 0.3)",
  Summer: "rgba(253, 216, 53, 0.3)",
  Autumn: "rgba(255, 111, 0, 0.3)"
};

const PHASE_DOT_COLORS = {
  Winter: "#C62828",
  Spring: "#2E7D32",
  Summer: "#FDD835",
  Autumn: "#E65100"
};

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

// ── Client-side cycle calculation ────────────────────────────────────────────
// Winter (period) phase only begins when the user manually confirms it by
// tapping the blood drop. Until then, the user stays in extended Autumn with
// a "period expected" message — even if the predicted date has passed.
// Calendar predictions (targetDate calls) still use pure math.
const calculateCyclePhaseClient = (cycleLength, periodLength, lastPeriodStart, targetDate) => {
  const lastPeriod = new Date(lastPeriodStart + 'T00:00:00');
  const today = targetDate ? new Date(targetDate + 'T00:00:00') : new Date();
  today.setHours(0, 0, 0, 0);
  const daysSinceStart = Math.floor((today - lastPeriod) / (1000 * 60 * 60 * 24));

  // For the CURRENT phase (no targetDate): if a full cycle has elapsed without
  // the user logging a new period, lock to Autumn with period_expected flag.
  if (!targetDate && daysSinceStart >= cycleLength) {
    const expectedDate = new Date(lastPeriod);
    expectedDate.setDate(expectedDate.getDate() + cycleLength);
    return {
      current_day: cycleLength,
      phase: "Autumn",
      phase_emoji: "🍂",
      phase_color: "#FF6F00",
      tagline: "Period expected, tap the drop when it arrives",
      next_period_date: expectedDate.toISOString().split('T')[0],
      days_until_period: 0,
      cycle_length: cycleLength,
      period_length: periodLength,
      period_expected: true,
    };
  }

  let cycleDay = (daysSinceStart % cycleLength) + 1;
  if (cycleDay <= 0) cycleDay = cycleLength + cycleDay;
  const ovulationDay = cycleLength - 14;
  let phase, phaseEmoji, phaseColor, tagline;
  if (cycleDay <= periodLength) {
    phase = "Winter"; phaseEmoji = "❄️"; phaseColor = "#E53935"; tagline = "Rest, comfort, alone time";
  } else if (cycleDay <= ovulationDay - 2) {
    phase = "Spring"; phaseEmoji = "🌱"; phaseColor = "#43A047"; tagline = "Rising energy, fresh start";
  } else if (cycleDay <= ovulationDay + 2) {
    phase = "Summer"; phaseEmoji = "☀️"; phaseColor = "#FDD835"; tagline = "Peak energy, put yourself out there";
  } else {
    phase = "Autumn"; phaseEmoji = "🍂"; phaseColor = "#FF6F00"; tagline = "Reflect, slow down, nourish yourself";
  }
  let daysUntilPeriod = cycleLength - cycleDay + 1;
  if (daysUntilPeriod > cycleLength) daysUntilPeriod -= cycleLength;
  const nextPeriod = new Date(today);
  nextPeriod.setDate(nextPeriod.getDate() + daysUntilPeriod);
  return {
    current_day: cycleDay,
    phase,
    phase_emoji: phaseEmoji,
    phase_color: phaseColor,
    tagline,
    next_period_date: nextPeriod.toISOString().split('T')[0],
    days_until_period: daysUntilPeriod,
    cycle_length: cycleLength,
    period_length: periodLength,
    period_expected: false,
  };
};

const generateCalendarDataClient = (cycleLength, periodLength, lastPeriodStart, year, month) => {
  const lastPeriod = new Date(lastPeriodStart + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDaysSince = Math.floor((today - lastPeriod) / (1000 * 60 * 60 * 24));
  const periodIsLate = todayDaysSince >= cycleLength;
  const todayStr = today.toISOString().split('T')[0];

  const lastDay = new Date(year, month, 0);
  const days = [];
  for (let day = 1; day <= lastDay.getDate(); day++) {
    const d = new Date(year, month - 1, day);
    const dateStr = d.toISOString().split('T')[0];
    const daysSince = Math.floor((d - lastPeriod) / (1000 * 60 * 60 * 24));
    const inCurrentUnconfirmed = periodIsLate
      && daysSince >= cycleLength
      && daysSince < 2 * cycleLength;

    let phaseInfo;
    if (inCurrentUnconfirmed) {
      if (d < today) {
        // Past days she was waiting: orange (Autumn)
        phaseInfo = { current_day: cycleLength, phase: "Autumn", phase_color: "#FF6F00" };
      } else {
        // Today onward: shift Winter to start from today
        phaseInfo = calculateCyclePhaseClient(cycleLength, periodLength, todayStr, dateStr);
      }
    } else {
      phaseInfo = calculateCyclePhaseClient(cycleLength, periodLength, lastPeriodStart, dateStr);
    }

    days.push({
      date: dateStr,
      cycle_day: phaseInfo.current_day,
      phase: phaseInfo.phase,
      phase_color: phaseInfo.phase_color,
      is_period_day: phaseInfo.phase === "Winter",
      is_logged: false,
    });
  }
  return days;
};


// ── Partner View ──────────────────────────────────────────────────────────────
// Shown when someone visits /partner/TOKEN — fully read-only, no auth needed.

const PARTNER_PHASE_COLORS = {
  Winter: { bg: 'rgba(229,57,53,0.13)', text: '#E53935', emoji: '❄️' },
  Spring: { bg: 'rgba(67,160,71,0.13)', text: '#43A047', emoji: null }, // uses flower img
  Summer: { bg: 'rgba(253,216,53,0.18)', text: '#F9A825', emoji: '☀️' },
  Autumn: { bg: 'rgba(255,111,0,0.13)', text: '#FF6F00', emoji: '🍂' },
};

const partnerGetPhaseForDay = (cycleDay, cycleLength, periodLength) => {
  const ov = cycleLength - 14;
  if (cycleDay <= periodLength) return 'Winter';
  if (cycleDay <= ov - 2) return 'Spring';
  if (cycleDay <= ov + 2) return 'Summer';
  return 'Autumn';
};

const partnerBuildCalendar = (year, month, lastPeriodStart, cycleLength, periodLength) => {
  const base = new Date(lastPeriodStart + 'T00:00:00');
  const firstDay = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startPad = firstDay.getDay(); // 0=Sun
  const today = new Date();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const diff = Math.floor((date - base) / 86400000);
    const cycleDay = ((diff % cycleLength) + cycleLength) % cycleLength + 1;
    const phase = partnerGetPhaseForDay(cycleDay, cycleLength, periodLength);
    const isToday = date.toDateString() === today.toDateString();
    cells.push({ d, phase, isToday });
  }
  return cells;
};

const PartnerView = ({ token }) => {
  const [status, setStatus] = useState('loading'); // loading | active | invalid
  const [cycleData, setCycleData] = useState(null);
  const [viewMonth, setViewMonth] = useState(new Date());
  const [ownerName, setOwnerName] = useState('');

  useEffect(() => {
    console.log('PartnerView: resolving token:', token)
    getPartnerCycleData(token).then(data => {
      const row = Array.isArray(data) ? data[0] : data;
      if (row && row.cycle_length) {
        setCycleData(row)
        if (row.first_name) setOwnerName(row.first_name);
        setStatus('active')
      } else {
        setStatus('invalid')
      }
    })
  }, [token])

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white' }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'white', padding: '2rem', textAlign: 'center' }}>
        <h1 className="hero-wordmark-gradient" style={{ marginBottom: '1rem' }}>ILIK</h1>
        <p style={{ color: '#9E9E9E', fontSize: '1rem' }}>This link is no longer active.</p>
      </div>
    );
  }

  const { last_period_start_date, cycle_length, period_length } = cycleData;
  const partnerName = ownerName ? `${ownerName}'s` : 'Her';
  const ov = cycle_length - 14;
  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const cells = partnerBuildCalendar(year, month, last_period_start_date, cycle_length, period_length);
  const monthName = viewMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const seasons = [
    {
      name: 'Winter', emoji: '❄️', color: '#E53935', bg: 'rgba(229,57,53,0.08)',
      subtitle: 'Period', days: `Day 1–${period_length}`,
      inside: 'Her body is menstruating. Energy and mood are at their lowest point in the cycle. She may feel physically uncomfortable, crampy, or drained.',
      feel: ['Low energy', 'Needs space', 'Physically tender', 'Withdrawn'],
      support: 'Keep things calm and quiet. Offer warmth, comfort food, or a hot water bottle without being asked. Reduce social plans if you can. Don\'t take it personally if she wants alone time.',
      closing: 'She needs rest and support.',
    },
    {
      name: 'Spring', emoji: null, color: '#43A047', bg: 'rgba(67,160,71,0.08)',
      subtitle: 'Follicular', days: `Day ${period_length + 1}–${ov - 2}`,
      inside: 'Her oestrogen is rising and her energy is coming back. She\'ll feel more social, optimistic, and open to new things.',
      feel: ['More talkative', 'Upbeat', 'Open to plans', 'Creative'],
      support: 'This is a great time to plan dates, try something new together, or have deeper conversations. Her mood is lifting and she\'ll be more receptive to ideas and spontaneity.',
      closing: 'Match her energy. She\'s ready to reconnect.',
    },
    {
      name: 'Summer', emoji: '☀️', color: '#F9A825', bg: 'rgba(253,216,53,0.12)',
      subtitle: 'Ovulation', days: `Day ${ov - 2}–${ov + 2}`,
      inside: 'She\'s at her peak. Confidence, energy, and connection are highest. She may seem more outgoing and expressive than usual.',
      feel: ['Confident', 'Affectionate', 'Sociable', 'High energy'],
      support: 'Enjoy this window. She\'s at her most communicative and present. If there\'s something important to discuss or decide together, this is the best time.',
      closing: 'She\'s shining. Show up and be present with her.',
    },
    {
      name: 'Autumn', emoji: '🍂', color: '#FF6F00', bg: 'rgba(255,111,0,0.08)',
      subtitle: 'Luteal', days: `Day ${ov + 3}–until next period arrives`,
      inside: 'Progesterone rises and her nervous system becomes more sensitive. She may feel more emotional, irritable, or easily overwhelmed. This is PMS territory.',
      feel: ['More sensitive', 'Easily frustrated', 'Needs reassurance', 'Lower patience'],
      support: 'Be patient and don\'t take mood changes personally. Avoid starting arguments or bringing up stressful topics. Small gestures of care go a long way. She may not know why she feels off, but she does.',
      closing: 'She\'s not being difficult. Her body is doing a lot right now.',
    },
  ];

  return (
    <div style={{ background: 'white', minHeight: '100vh', maxWidth: 480, margin: '0 auto', padding: '0 0 60px', fontFamily: "'Nunito', sans-serif" }} data-testid="partner-view">
      {/* Header */}
      <div style={{ textAlign: 'center', padding: '48px 24px 24px' }}>
        <h1 className="hero-wordmark-gradient" style={{ marginBottom: '0.1rem' }}>ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', margin: '0 0 8px' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
        <p style={{ fontSize: '0.8rem', color: '#9E9E9E', margin: 0 }}>You're viewing {partnerName} cycle</p>
      </div>

      {/* Divider */}
      <div style={{ height: 1, background: '#F0F0F0', margin: '0 24px 32px' }} />

      {/* Calendar Section */}
      <div style={{ padding: '0 16px', marginBottom: '40px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', padding: '0 8px' }}>
          <button onClick={() => setViewMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '1.2rem', color: '#757575' }}>‹</button>
          <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 600, fontSize: '0.95rem', color: '#212121' }}>{monthName}</span>
          <button onClick={() => setViewMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', fontSize: '1.2rem', color: '#757575' }}>›</button>
        </div>
        {/* Day headers */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', marginBottom: '4px' }}>
          {DAYS.map(d => <div key={d} style={{ textAlign: 'center', fontSize: '0.7rem', color: '#9E9E9E', fontWeight: 600, padding: '4px 0' }}>{d}</div>)}
        </div>
        {/* Day cells */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gap: '3px' }}>
          {cells.map((cell, i) => {
            if (!cell) return <div key={i} />;
            const pc = PARTNER_PHASE_COLORS[cell.phase];
            return (
              <div key={i} style={{ background: pc.bg, borderRadius: 8, aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', border: cell.isToday ? `2px solid ${pc.text}` : 'none' }}>
                <span style={{ fontSize: '0.8rem', fontWeight: cell.isToday ? 700 : 400, color: cell.isToday ? pc.text : '#424242' }}>{cell.d}</span>
              </div>
            );
          })}
        </div>
        {/* Phase legend */}
        <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' }}>
          {Object.entries(PARTNER_PHASE_COLORS).map(([phase, pc]) => (
            <div key={phase} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', color: '#757575' }}>
              <div style={{ width: 10, height: 10, borderRadius: 3, background: pc.bg, border: `1.5px solid ${pc.text}` }} />
              {phase}
            </div>
          ))}
        </div>
      </div>

      {/* Seasons Section */}
      <div style={{ padding: '0 16px' }}>
        <p style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: '#212121', padding: '0 8px', marginBottom: '16px' }}>{partnerName} 4 Seasons</p>
        {seasons.map(s => (
          <div key={s.name} style={{ background: s.bg, borderRadius: 16, padding: '20px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
              {s.name === 'Spring'
                ? <img src={SPRING_FLOWER_URL} alt="Spring" style={{ width: 22, height: 22 }} />
                : <span style={{ fontSize: '1.2rem' }}>{s.emoji}</span>
              }
              <div>
                <span style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', color: s.color }}>{s.name}</span>
                <span style={{ fontSize: '0.75rem', color: '#9E9E9E', marginLeft: 8 }}>{s.subtitle === 'Period' ? <><Droplet size={10} fill="#E53935" color="#E53935" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />{s.subtitle}</> : s.subtitle} · {s.days}</span>
              </div>
            </div>
            <p style={{ fontSize: '0.875rem', color: '#424242', marginBottom: '10px', lineHeight: 1.5 }}>{s.inside}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '10px' }}>
              {s.feel.map(t => <span key={t} style={{ background: 'white', borderRadius: 99, padding: '3px 10px', fontSize: '0.75rem', color: s.color, border: `1px solid ${s.color}30` }}>{t}</span>)}
            </div>
            <p style={{ fontSize: '0.8rem', color: '#616161', lineHeight: 1.5, marginBottom: '10px' }}>{s.support}</p>
            <p style={{ fontSize: '0.8rem', fontStyle: 'italic', color: s.color, margin: 0 }}>{s.closing}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

// Welcome Auth Screen — shown when no active session
const WelcomeAuthScreen = ({ onGetStarted, onSignIn }) => (
  <div className="onboarding-hero-screen-white fade-in" data-testid="welcome-auth-screen">
    <div className="hero-content-white">
      <h1 className="hero-wordmark-gradient">ILIK</h1>
      <p className="app-tagline" style={{ textAlign: 'center', marginTop: '-0.4rem', marginBottom: '1rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
      <div className="hero-emoji-row">
        <span>❄️</span>
        <img src={SPRING_FLOWER_URL} alt="Spring" className="hero-spring-icon" />
        <span>☀️</span>
        <span>🍂</span>
      </div>
      <button className="hero-pill-btn orange" onClick={onGetStarted} data-testid="get-started-btn">
        Get Started
      </button>
      <button className="auth-text-link" onClick={onSignIn} data-testid="already-have-account-link">
        I already have an account
      </button>
    </div>
  </div>
);

// Sign In Screen
const SignInScreen = ({ onSignInComplete, onBack, onSignUp }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotSubmitting, setForgotSubmitting] = useState(false);

  const handleForgotSubmit = async () => {
    if (!isValidEmail(forgotEmail.trim())) return;
    setForgotSubmitting(true);
    try {
      await supabase.auth.resetPasswordForEmail(forgotEmail.trim(), {
        redirectTo: window.location.origin
      });
    } catch (err) {
      // Never reveal whether email exists — always show confirmation
    } finally {
      setForgotSent(true);
      setForgotSubmitting(false);
    }
  };

  const handleSignIn = async () => {
    if (!email.trim() || !password) {
      setError('Please enter your email and password.');
      return;
    }
    setIsSubmitting(true);
    setError('');
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
      if (authError) {
        setError('Incorrect email or password. Please try again.');
        setIsSubmitting(false);
        return;
      }
      await onSignInComplete();
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  if (showForgot) {
    return (
      <div className="onboarding-setup-screen fade-in" data-testid="forgot-password-screen">
        <div className="setup-content account-creation">
          <button
            className="auth-back-btn"
            onClick={() => { setShowForgot(false); setForgotEmail(''); setForgotSent(false); }}
            data-testid="forgot-back-btn"
          >
            ← Back
          </button>
          <h1 className="account-wordmark-gradient">ILIK</h1>
          <p className="app-tagline" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1.5rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
          <h2 className="setup-heading">Reset password</h2>
          {!forgotSent ? (
            <>
              <div className="account-form">
                <div className="account-input-wrapper">
                  <input
                    type="email"
                    placeholder="Email"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    className="account-input account-input-icon"
                    data-testid="forgot-email-input"
                  />
                  {isValidEmail(forgotEmail) && <span className="input-valid-check"><Check size={15} /></span>}
                </div>
              </div>
              <button
                className="hero-pill-btn orange"
                onClick={handleForgotSubmit}
                disabled={forgotSubmitting || !isValidEmail(forgotEmail.trim())}
                data-testid="forgot-submit-btn"
              >
                {forgotSubmitting ? 'Sending…' : 'Send reset link'}
              </button>
            </>
          ) : (
            <p className="auth-forgot-message" style={{ textAlign: 'center', marginTop: '2rem', lineHeight: 1.6 }} data-testid="forgot-confirmation">
              Check your inbox, we've sent a reset link to <strong>{forgotEmail}</strong>
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-setup-screen fade-in" data-testid="signin-screen">
      <div className="setup-content account-creation">
        <button className="auth-back-btn" onClick={onBack} data-testid="signin-back-btn">
          ← Back
        </button>
        <h1 className="account-wordmark-gradient">ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1.5rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
        <h2 className="setup-heading">Welcome back</h2>
        <div className="account-form">
          <div className="account-input-wrapper">
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(''); }}
              className="account-input account-input-icon"
              data-testid="signin-email-input"
            />
            {isValidEmail(email) && <span className="input-valid-check"><Check size={15} /></span>}
          </div>
          <div className="account-input-wrapper">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => { setPassword(e.target.value); setError(''); }}
              className="account-input"
              data-testid="signin-password-input"
            />
          </div>
          {error && <p className="account-error">{error}</p>}
        </div>
        <button
          className="hero-pill-btn orange"
          onClick={handleSignIn}
          disabled={isSubmitting}
          data-testid="signin-submit-btn"
        >
          {isSubmitting ? 'Signing in…' : 'Sign In'}
        </button>
        <button className="auth-text-link" onClick={() => { setShowForgot(true); setForgotEmail(email); }} data-testid="forgot-password-btn">
          Forgot password?
        </button>
        <p style={{ marginTop: '1rem', fontSize: '0.9rem', color: '#999' }} data-testid="signup-prompt">
          Don't have an account?{' '}
          <span style={{ color: '#FF8F0B', cursor: 'pointer', fontWeight: 600 }} onClick={onSignUp} data-testid="goto-signup-from-signin">Sign up</span>
        </p>
      </div>
    </div>
  );
};


// Seasons Intro — reusable for onboarding step 8 and Settings preview
const SeasonsIntroContent = ({ fadeClass = 'fade-in', onAction, actionLabel = "I'm ready →", showBack, onBack }) => {
  const introArc = (startPct, endPct) => {
    const r = 80, inner = 65, cx = 95, cy = 95;
    const toRad = (pct) => ((pct / 100) * 360 - 90) * Math.PI / 180;
    const sr = toRad(startPct), er = toRad(endPct);
    const x1 = cx + r * Math.cos(sr), y1 = cy + r * Math.sin(sr);
    const x2 = cx + r * Math.cos(er), y2 = cy + r * Math.sin(er);
    const x3 = cx + inner * Math.cos(er), y3 = cy + inner * Math.sin(er);
    const x4 = cx + inner * Math.cos(sr), y4 = cy + inner * Math.sin(sr);
    const la = (endPct - startPct) > 50 ? 1 : 0;
    return `M ${x1} ${y1} A ${r} ${r} 0 ${la} 1 ${x2} ${y2} L ${x3} ${y3} A ${inner} ${inner} 0 ${la} 0 ${x4} ${y4} Z`;
  };

  const labelStyle = { position: 'absolute', display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1.3 };

  return (
    <div className={`onboarding-setup-screen ${fadeClass}`} data-testid="seasons-intro-screen">
      {showBack && (
        <button onClick={onBack} style={{ position: 'absolute', top: 20, left: 16, background: 'none', border: 'none', cursor: 'pointer', color: '#757575', display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.95rem' }} data-testid="preview-back-btn">
          <ChevronLeft size={20} /> Back
        </button>
      )}
      <div className="setup-content">
        <h1 className="account-wordmark-gradient">ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', marginTop: 0, marginBottom: '1.5rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
        <h2 className="setup-heading">Your cycle has 4 seasons</h2>
        <p className="setup-subtext" style={{ marginBottom: '0.75rem' }}>Each season has a mood. ILIK helps you plan with it.</p>

        {/* Ring chart with labeled icons at cardinal points */}
        <div style={{ width: 190, height: 190, margin: '16px auto 0', padding: 30, boxSizing: 'content-box' }}>
          <div style={{ width: 190, height: 190, position: 'relative' }}>
            <svg viewBox="-30 -30 250 250" style={{ width: '100%', height: '100%', overflow: 'visible' }}>
              <path d={introArc(0, 24)} fill="#E53935" />
              <path d={introArc(26, 49)} fill="#43A047" />
              <path d={introArc(51, 74)} fill="#FDD835" />
              <path d={introArc(76, 99)} fill="#FF6F00" />
            </svg>

            {/* Top — Winter */}
            <div style={{ ...labelStyle, left: '50%', top: -38, transform: 'translateX(-50%)', textAlign: 'center' }} data-testid="intro-season-winter">
              <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Droplet size={16} fill="#E53935" color="#E53935" />
                <span style={{ fontSize: 16 }}>❄️</span>
              </div>
              <span style={{ fontWeight: 700, fontSize: 11, color: '#E53935' }}>Winter</span>
              <span style={{ fontSize: 10, color: '#999' }}>Your period</span>
            </div>

            {/* Right — Spring */}
            <div style={{ ...labelStyle, top: '50%', left: 'calc(100% + 8px)', transform: 'translateY(-50%)', alignItems: 'flex-start', textAlign: 'left' }} data-testid="intro-season-spring">
              <img src={SPRING_FLOWER_URL} alt="Spring" style={{ width: 16, height: 16 }} />
              <span style={{ fontWeight: 700, fontSize: 11, color: '#43A047' }}>Spring</span>
              <span style={{ fontSize: 10, color: '#999' }}>Rising again</span>
            </div>

            {/* Bottom — Summer */}
            <div style={{ ...labelStyle, left: '50%', bottom: -38, transform: 'translateX(-50%)', textAlign: 'center' }} data-testid="intro-season-summer">
              <span style={{ fontSize: 16 }}>☀️</span>
              <span style={{ fontWeight: 700, fontSize: 11, color: '#FDD835' }}>Summer</span>
              <span style={{ fontSize: 10, color: '#999' }}>Body is most active</span>
            </div>

            {/* Left — Autumn */}
            <div style={{ ...labelStyle, top: '50%', right: 'calc(100% + 8px)', transform: 'translateY(-50%)', alignItems: 'flex-end', textAlign: 'right' }} data-testid="intro-season-autumn">
              <span style={{ fontSize: 16 }}>🍂</span>
              <span style={{ fontWeight: 700, fontSize: 11, color: '#FF6F00' }}>Autumn</span>
              <span style={{ fontSize: 10, color: '#999' }}>Before your period</span>
            </div>
          </div>
        </div>

        <button className="hero-pill-btn orange" onClick={onAction} data-testid="seasons-intro-cta">
          {actionLabel}
        </button>
      </div>
    </div>
  );
};


const OnboardingScreen = ({ onComplete, onSwitchToSignIn = () => {}, startAtCycleSetup }) => {
  const [step, setStep] = useState(startAtCycleSetup ? 4 : 7);
  const [fadeClass, setFadeClass] = useState('fade-in');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [nameError, setNameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [signUpError, setSignUpError] = useState('');
  const [formData, setFormData] = useState({
    cycle_length: 28,
    period_length: 5,
    last_period_start: new Date().toISOString().split('T')[0]
  });

  // Fetch email from session when user is already logged in (cycle setup mode)
  useEffect(() => {
    if (startAtCycleSetup && supabase) {
      supabase.auth.getUser().then(({ data: { user } }) => {
        if (user?.email) setEmail(user.email);
      });
    }
  }, [startAtCycleSetup]);

  // Fade transition helper
  const goToStep = (nextStep) => {
    setFadeClass('fade-out');
    setTimeout(() => {
      setStep(nextStep);
      setFadeClass('fade-in');
    }, 300);
  };

  // Stepper handlers
  const incrementCycleLength = () => {
    if (formData.cycle_length < 45) {
      setFormData({ ...formData, cycle_length: formData.cycle_length + 1 });
    }
  };

  const decrementCycleLength = () => {
    if (formData.cycle_length > 21) {
      setFormData({ ...formData, cycle_length: formData.cycle_length - 1 });
    }
  };

  const incrementPeriodLength = () => {
    if (formData.period_length < 10) {
      setFormData({ ...formData, period_length: formData.period_length + 1 });
    }
  };

  const decrementPeriodLength = () => {
    if (formData.period_length > 2) {
      setFormData({ ...formData, period_length: formData.period_length - 1 });
    }
  };

  const handleSubmit = async () => {
    // Validate name
    if (!firstName.trim()) { setNameError('Please enter your first name'); return; }
    // Validate email
    if (!email.trim()) { setEmailError('Please enter your email'); return; }
    if (!isValidEmail(email)) { setEmailError('Please enter a valid email address'); return; }
    // Validate password
    if (!password) { setPasswordError('Please create a password'); return; }
    if (password.length < 6) { setPasswordError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setPasswordError("Passwords don't match"); return; }

    setSignUpError('');
    console.log('🔍 Starting sign up...');

    try {
      // Sign up with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: email.trim(),
        password,
      });

      if (authError) {
        if (authError.message?.toLowerCase().includes('already registered') || authError.message?.toLowerCase().includes('already exists')) {
          setSignUpError('already_exists');
        } else {
          setSignUpError('Sign up failed. Please try again.');
        }
        return;
      }

      toast.success("Account created!");
      goToStep(4);
    } catch (error) {
      toast.error("Failed to create account");
      console.error('❌ Onboarding save error:', error);
    }
  };

  // Save cycle data after onboarding detail steps are complete
  const handleCycleDataSave = async () => {
    // Save to localStorage (always succeeds, offline fallback)
    localStorage.setItem('user_data', JSON.stringify({
      email: email.trim(),
      first_name: firstName.trim(),
      last_period_start_date: formData.last_period_start,
      cycle_length: formData.cycle_length,
      period_length: formData.period_length,
    }));

    // 1. PRIMARY — Save to Supabase first
    try {
      await saveUserCycleToSupabase(email.trim(), formData.last_period_start, formData.cycle_length, formData.period_length);
    } catch (err) {
      console.error('Supabase save error:', err);
    }

    // Save first_name to Supabase Auth user metadata (identity layer, not health data)
    try {
      await supabase.auth.updateUser({ data: { first_name: firstName.trim() } });
    } catch (err) {
      console.warn('Auth metadata save error:', err.message);
    }

    // 2. SECONDARY — Sync to backend (non-blocking)
    axios.post(`${API}/settings`, { ...formData, email: email.trim() }).catch(err => console.warn('Backend save error:', err.message));

    // Always proceed — data is in Supabase + localStorage
    goToStep(8);
  };

  // SETUP STEP 1: Last Period
  if (step === 4) {
    return (
      <div className={`onboarding-setup-screen ${fadeClass}`} data-testid="onboarding-setup-step-1">
        <div className="setup-content">
          <div className="setup-progress">
            <span className="progress-dot active"></span>
            <span className="progress-dot"></span>
            <span className="progress-dot"></span>
          </div>
          <p className="setup-step-label">Step 1 of 3</p>
          <h2 className="setup-heading">When did your last period start?</h2>
          <p className="setup-subtext">An estimate is fine. You can update this anytime.</p>
          <div className="setup-date-picker">
            <input
              type="date"
              value={formData.last_period_start}
              onChange={(e) => setFormData({ ...formData, last_period_start: e.target.value })}
              className="setup-date-input"
              data-testid="last-period-input"
            />
          </div>
          <button 
            className="hero-pill-btn orange"
            onClick={() => goToStep(5)}
            data-testid="setup-step-1-next"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // SETUP STEP 2: Cycle Length
  if (step === 5) {
    return (
      <div className={`onboarding-setup-screen ${fadeClass}`} data-testid="onboarding-setup-step-2">
        <div className="setup-content">
          <div className="setup-progress">
            <span className="progress-dot"></span>
            <span className="progress-dot active"></span>
            <span className="progress-dot"></span>
          </div>
          <p className="setup-step-label">Step 2 of 3</p>
          <h2 className="setup-heading">About how long is your cycle?</h2>
          <p className="setup-subtext">Most cycles are between 25–35 days. 28 is a common starting point.</p>
          <div className="setup-stepper">
            <button 
              className="stepper-btn"
              onClick={decrementCycleLength}
              disabled={formData.cycle_length <= 21}
              data-testid="cycle-length-minus"
            >
              −
            </button>
            <span className="stepper-value" data-testid="cycle-length-value">{formData.cycle_length}</span>
            <button 
              className="stepper-btn"
              onClick={incrementCycleLength}
              disabled={formData.cycle_length >= 45}
              data-testid="cycle-length-plus"
            >
              +
            </button>
          </div>
          <p className="stepper-unit">days</p>
          <button 
            className="hero-pill-btn orange"
            onClick={() => goToStep(6)}
            data-testid="setup-step-2-next"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // SETUP STEP 3: Period Length
  if (step === 6) {
    return (
      <div className={`onboarding-setup-screen ${fadeClass}`} data-testid="onboarding-setup-step-3">
        <div className="setup-content">
          <div className="setup-progress">
            <span className="progress-dot"></span>
            <span className="progress-dot"></span>
            <span className="progress-dot active"></span>
          </div>
          <p className="setup-step-label">Step 3 of 3</p>
          <h2 className="setup-heading">How many days does your period last?</h2>
          <p className="setup-subtext">Typically 3–7 days.</p>
          <div className="setup-stepper">
            <button 
              className="stepper-btn"
              onClick={decrementPeriodLength}
              disabled={formData.period_length <= 2}
              data-testid="period-length-minus"
            >
              −
            </button>
            <span className="stepper-value" data-testid="period-length-value">{formData.period_length}</span>
            <button 
              className="stepper-btn"
              onClick={incrementPeriodLength}
              disabled={formData.period_length >= 10}
              data-testid="period-length-plus"
            >
              +
            </button>
          </div>
          <p className="stepper-unit">days</p>
          <button 
            className="hero-pill-btn orange"
            onClick={handleCycleDataSave}
            data-testid="setup-step-3-next"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // SETUP STEP 4: Account Creation (Email + Password)
  if (step === 7) {
    const passwordsMatch = password && confirmPassword && password === confirmPassword;
    const canSubmit = firstName.trim() && isValidEmail(email) && password.length >= 6 && passwordsMatch;

    return (
      <div className={`onboarding-setup-screen ${fadeClass}`} data-testid="onboarding-setup-step-4">
        <div className="setup-content account-creation">
          <h1 className="account-wordmark-gradient">ILIK</h1>
          <p className="app-tagline" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1.5rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
          <h2 className="setup-heading">Create your account</h2>

          <div className="account-form">
            <div className="account-input-wrapper">
              <input
                type="text"
                placeholder="First name"
                value={firstName}
                onChange={(e) => { setFirstName(e.target.value); setNameError(''); }}
                className="account-input account-input-icon"
                data-testid="account-name-input"
              />
              {firstName.trim() && <span className="input-valid-check"><Check size={15} /></span>}
              {nameError && <p className="account-error">{nameError}</p>}
            </div>
            <div className="account-input-wrapper">
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(''); setSignUpError(''); }}
                className="account-input account-input-icon"
                data-testid="account-email-input"
              />
              {isValidEmail(email) && <span className="input-valid-check"><Check size={15} /></span>}
              {emailError && <p className="account-error">{emailError}</p>}
            </div>
            <div className="account-input-wrapper">
              <input
                type="password"
                placeholder="Create password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                className="account-input account-input-icon"
                data-testid="account-password-input"
              />
              {password.length >= 6 && <span className="input-valid-check"><Check size={15} /></span>}
            </div>
            <div className="account-input-wrapper">
              <input
                type="password"
                placeholder="Confirm password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(''); }}
                className="account-input account-input-icon"
                data-testid="account-confirm-password-input"
              />
              {confirmPassword && password === confirmPassword && password.length >= 6 && <span className="input-valid-check"><Check size={15} /></span>}
              {passwordError && <p className="account-error">{passwordError}</p>}
              {confirmPassword && password !== confirmPassword && !passwordError && (
                <p className="account-error">Passwords don't match</p>
              )}
            </div>
            {signUpError === 'already_exists' && (
              <div className="account-error" style={{ textAlign: 'center' }}>
                This email already has an account.{' '}
                <span className="auth-inline-link" onClick={onSwitchToSignIn} data-testid="switch-to-signin">
                  Sign in instead?
                </span>
              </div>
            )}
            {signUpError && signUpError !== 'already_exists' && (
              <p className="account-error" style={{ textAlign: 'center' }}>{signUpError}</p>
            )}
          </div>

          <button
            className="hero-pill-btn orange"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{ opacity: canSubmit ? 1 : 0.5 }}
            data-testid="create-account-btn"
          >
            next
          </button>
          <button className="auth-text-link" onClick={onSwitchToSignIn} data-testid="goto-signin-from-signup">
            I already have an account
          </button>
        </div>
      </div>
    );
  }

  if (step === 8) {
    return <SeasonsIntroContent fadeClass={fadeClass} onAction={async () => { try { await markSeasonsIntroSeen(); } catch {} onComplete(); }} />;
  }

  return null;
};

// Day Detail Modal with Event Management
const MOOD_EMOJIS = ['🔥','😍','🥳','💪','😂','😎','🥰','😴','😌','😶‍🌫️','🤯','😤','😢','🥵','😰','🫠','🤒','😶','🦁','✨','🤓','🤪','😒','😖','🤑','🤧','😵‍💫','💃🏽'];

const DayDetailModal = ({ isOpen, onClose, dayData, events, onAddEvent, onDeleteEvent, onShowCapacityMessage, bypassCapacity, mood, onMoodChange }) => {
  const [showEventForm, setShowEventForm] = useState(false);
  const [showMoodPicker, setShowMoodPicker] = useState(false);
  const [eventForm, setEventForm] = useState({
    title: '',
    time: '',
    notes: '',
    reminder: ''
  });

  if (!isOpen || !dayData) return null;

  const date = new Date(dayData.date);
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  
  const formattedDate = `${dayNames[date.getDay()]} ${monthNames[date.getMonth()]} ${date.getDate()}`;
  const phaseConfig = PHASE_CONFIG[dayData.phase];
  const dayEvents = events.filter(e => e.date === dayData.date);
  const currentEventCount = dayEvents.length;

  const handleAddEventClick = () => {
    const eventCount = events.filter(e => e.date === dayData.date).length;
    if (!bypassCapacity && eventCount >= phaseConfig.maxEvents) {
      onClose(); // Close day modal first
      onShowCapacityMessage(dayData.phase);
    } else {
      // Set default time to next hour
      const now = new Date();
      const nextHour = new Date(now.setHours(now.getHours() + 1, 0, 0, 0));
      const timeStr = `${String(nextHour.getHours()).padStart(2, '0')}:00`;
      setEventForm({ title: '', time: timeStr, notes: '', reminder: '' });
      setShowEventForm(true);
    }
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim()) {
      toast.error("Please enter an event title");
      return;
    }
    await onAddEvent({
      date: dayData.date,
      title: eventForm.title,
      time: eventForm.time,
      notes: eventForm.notes,
      reminder: eventForm.reminder,
      phase: dayData.phase
    });
    setShowEventForm(false);
    setEventForm({ title: '', time: '', notes: '', reminder: '' });
  };

  const reminderOptions = ["5 mins before", "1 hour before", "3 hours before", "Morning of", "1 day before"];

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="day-detail-modal">
      <div className="modal-content day-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-handle"></div>
        
        {!showEventForm ? (
          <>
            <div className="modal-header">
              <div>
                <h2 className="modal-date">{formattedDate}</h2>
                <div className="modal-meta">
                  <span className="modal-cycle-day-pill">Cycle Day {dayData.cycle_day}</span>
                  <span className="modal-phase-pill" style={{ backgroundColor: phaseConfig.bgColor, color: phaseConfig.color }}>
                    {dayData.phase === 'Winter' ? (
                      <><Droplet size={14} fill="#E53935" color="#E53935" style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '1px' }} />{phaseConfig.emoji} </>
                    ) : dayData.phase === 'Spring' ? (
                      <img src={SPRING_FLOWER_URL} alt="Spring" style={{ width: '14px', height: '14px', display: 'inline-block', verticalAlign: 'middle', marginRight: '3px' }} />
                    ) : (
                      <>{phaseConfig.emoji} </>
                    )}
                    {dayData.phase}
                  </span>
                </div>
              </div>
              <button className="modal-close" onClick={onClose} data-testid="close-modal-btn">
                <X size={24} />
              </button>
            </div>

            <p className="modal-tagline">{phaseConfig.tagline}</p>

            {/* Mood emoji selector */}
            {(() => {
              const selected = mood ? mood.split(',').filter(Boolean) : [];
              const hasSelection = selected.length > 0;
              const canAddMore = selected.length < 3;
              const showFullRow = !hasSelection || showMoodPicker;

              return (
                <div className="modal-section mood-section" data-testid="mood-section">
                  <span className="section-title">HOW YOU FEEL TODAY?</span>

                  {/* Selected emojis display */}
                  {hasSelection && !showMoodPicker && (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 16, padding: '10px 0 4px' }} data-testid="mood-selected-display">
                      {selected.map((em) => (
                        <div key={em} style={{ position: 'relative', display: 'inline-flex' }}>
                          <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: phaseConfig.color + '33', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.6rem' }}>
                            {em}
                          </div>
                          <button
                            onClick={() => onMoodChange(dayData.date, em)}
                            style={{ position: 'absolute', top: -4, right: -4, width: 18, height: 18, borderRadius: '50%', border: 'none', background: '#eee', color: '#999', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0, lineHeight: 1 }}
                            data-testid={`mood-remove-${em}`}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* "+ Add another feeling" link */}
                  {hasSelection && !showMoodPicker && canAddMore && (
                    <button
                      onClick={() => setShowMoodPicker(true)}
                      style={{ display: 'block', margin: '6px auto 0', background: 'none', border: 'none', color: '#999', fontSize: 12, cursor: 'pointer', padding: '4px 8px' }}
                      data-testid="mood-add-another"
                    >+ Add another feeling</button>
                  )}

                  {/* Full 20-emoji scrollable row */}
                  {showFullRow && (
                    <div className="mood-scroll-row" data-testid="mood-scroll-row">
                      {MOOD_EMOJIS.map((emoji) => {
                        const isChosen = selected.includes(emoji);
                        return (
                          <button
                            key={emoji}
                            className="mood-emoji"
                            style={isChosen ? { backgroundColor: phaseConfig.color + '33', transform: 'scale(1.3)', opacity: 1 } : undefined}
                            onClick={() => { onMoodChange(dayData.date, emoji); setShowMoodPicker(false); }}
                            data-testid={`mood-emoji-${emoji}`}
                          >
                            {emoji}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            <div className="modal-section">
              <div className="section-header">
                <span className="section-title">YOUR EVENTS</span>
                <button className="add-event-btn" onClick={handleAddEventClick} data-testid="add-event-btn">
                  <Plus size={14} />
                  <span>Add Event</span>
                </button>
              </div>
              {dayEvents.length === 0 ? (
                <div className="events-empty">No events yet</div>
              ) : (
                <div className="events-list">
                  {dayEvents.map((event) => (
                    <div key={event.id} className="event-row">
                      <span className="event-dot" style={{ backgroundColor: phaseConfig.color }}></span>
                      <span className="event-title">{event.title}</span>
                      {event.time && <span className="event-time">{event.time}</span>}
                      <button className="event-delete" onClick={() => onDeleteEvent(event.id)} data-testid={`delete-event-${event.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-section">
              <span className="section-title">SUGGESTIONS FOR TODAY</span>
              <div className="suggestions-pills">
                {phaseConfig.suggestions.map((suggestion, index) => (
                  <span key={index} className="suggestion-pill" style={{ backgroundColor: phaseConfig.bgColor, color: phaseConfig.color }}>
                    {suggestion}
                  </span>
                ))}
              </div>
            </div>
          </>
        ) : (
          <div className="event-form" data-testid="event-form">
            <div className="form-header">
              <button className="back-to-day" onClick={() => setShowEventForm(false)}>
                <ChevronLeft size={20} />
                <span>Back</span>
              </button>
              <h3 className="form-title">Add Event</h3>
            </div>

            <div className="form-field">
              <Input
                placeholder="What do you want to do?"
                value={eventForm.title}
                onChange={(e) => setEventForm({...eventForm, title: e.target.value})}
                className="event-title-input"
                data-testid="event-title-input"
              />
            </div>

            <div className="form-field">
              <Label className="form-label">Date</Label>
              <div className="date-display-pill">{formattedDate}</div>
            </div>

            <div className="form-field">
              <Label className="form-label">Time</Label>
              <Input
                type="time"
                value={eventForm.time}
                onChange={(e) => setEventForm({...eventForm, time: e.target.value})}
                className="time-input"
                data-testid="event-time-input"
              />
            </div>

            <div className="form-field">
              <Label className="form-label">Notes</Label>
              <textarea
                placeholder="Any notes for yourself?"
                value={eventForm.notes}
                onChange={(e) => setEventForm({...eventForm, notes: e.target.value})}
                className="notes-textarea"
                data-testid="event-notes-input"
              />
            </div>

            <div className="form-field">
              <Label className="form-label"><Bell size={14} /> Remind me</Label>
              <div className="reminder-options">
                {reminderOptions.map((option) => (
                  <button
                    key={option}
                    className={`reminder-pill ${eventForm.reminder === option ? 'selected' : ''}`}
                    onClick={() => setEventForm({...eventForm, reminder: option})}
                    data-testid={`reminder-${option.replace(/\s/g, '-')}`}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            <Button className="save-event-btn" onClick={handleSaveEvent} data-testid="save-event-btn">
              Save Event
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

// Capacity Limit Modal
const CapacityModal = ({ isOpen, phase, onClose, onPlanAnyway }) => {
  if (!isOpen || !phase) return null;
  
  const config = PHASE_CONFIG[phase];
  
  return (
    <div className="capacity-overlay" data-testid="capacity-modal">
      <div className="capacity-modal">
        {phase === 'Spring' ? (
          <img src={SPRING_FLOWER_URL} alt="Spring" className="capacity-flower-icon" />
        ) : (
          <span className="capacity-emoji">{config.emoji}</span>
        )}
        <h2 className="capacity-heading">{config.capacityMessage.heading}</h2>
        <p className="capacity-message">{config.capacityMessage.message}</p>
        <button 
          className="capacity-button" 
          style={{ backgroundColor: config.color, color: phase === 'Summer' ? '#333' : 'white' }}
          onClick={onClose}
          data-testid="capacity-dismiss-btn"
        >
          {config.capacityMessage.button}
        </button>
        <button
          className="capacity-plan-anyway-btn"
          onClick={onPlanAnyway}
          data-testid="capacity-plan-anyway-btn"
        >
          Plan anyway
        </button>
      </div>
    </div>
  );
};

// Premium Paywall Modal
// Share with Partner Screen
const ShareWithPartnerModal = ({ isOpen, onClose, userName }) => {
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [shared, setShared] = useState(false);
  const [revoking, setRevoking] = useState(false);

  const appBase = window.location.origin;

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    setShared(false);
    getPartnerLink().then(existing => {
      setToken(existing);
      setLoading(false);
    });
  }, [isOpen]);

  const handleGenerate = async () => {
    setGenerating(true);
    const newToken = await createPartnerLink();
    setToken(newToken);
    setGenerating(false);
  };

  const partnerLink = token ? `${appBase}/partner/${token}` : null;

  const handleShare = async () => {
    if (!partnerLink) return;
    if (navigator.share) {
      try {
        const shareText = userName
          ? `Here's The 4 Seasons of ${userName}`
          : 'Here\'s my 4 Seasons';
        await navigator.share({
          title: 'ILIK',
          text: shareText,
          url: partnerLink,
        });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(partnerLink);
      setShared(true);
      setTimeout(() => setShared(false), 2500);
    } catch {}
  };

  const handleRevoke = async () => {
    setRevoking(true);
    const ok = await revokePartnerLink();
    if (ok) setToken(null);
    setRevoking(false);
  };

  if (!isOpen) return null;

  return (
    <div className="paywall-overlay" data-testid="share-partner-modal">
      <div className="paywall-modal">
        <button className="paywall-close" onClick={onClose} data-testid="share-partner-close">
          <X size={24} />
        </button>

        <h1 className="paywall-logo">ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1.5rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>&#174;</sup></p>

        <h2 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.2rem', fontWeight: 700, color: '#212121', marginBottom: '0.5rem' }}>
          Share your cycle
        </h2>
        <p style={{ fontSize: '0.85rem', color: '#757575', lineHeight: 1.6, marginBottom: '2rem', maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>
          Your partner gets a read-only view of your calendar and seasons. They cannot edit anything.
        </p>

        {loading ? (
          <p style={{ color: '#9E9E9E', fontSize: '0.9rem' }}>Loading...</p>
        ) : !token ? (
          <button
            className="paywall-cta"
            onClick={handleGenerate}
            disabled={generating}
            data-testid="generate-partner-link-btn"
          >
            {generating ? 'Creating link...' : 'Create a sharing link'}
          </button>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
            <button
              onClick={handleShare}
              data-testid="share-partner-btn"
              style={{ width: '100%', maxWidth: 280, padding: '14px 20px', borderRadius: 24, border: 'none', background: 'linear-gradient(135deg, #FF8F0B, #FF2D78)', color: '#fff', fontWeight: 700, fontSize: '0.95rem', fontFamily: "'Nunito', sans-serif", cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'opacity 0.15s' }}
            >
              {shared ? (
                <><Check size={18} /> Link copied</>
              ) : (
                <><ExternalLink size={18} /> Share with your partner</>
              )}
            </button>
            <button
              onClick={handleRevoke}
              disabled={revoking}
              data-testid="revoke-partner-link-btn"
              style={{ background: 'none', border: 'none', color: '#E53935', fontSize: '0.8rem', fontFamily: "'Nunito', sans-serif", fontWeight: 600, cursor: 'pointer', padding: '8px 0', opacity: revoking ? 0.5 : 1 }}
            >
              {revoking ? 'Revoking...' : 'Stop sharing'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const PaywallModal = ({ isOpen, onClose, onJoin }) => {
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(false);
  if (!isOpen) return null;
  
  const handleJoin = async () => {
    setIsCheckoutLoading(true);
    setCheckoutError(false);
    try {
      // Native (iOS): use RevenueCat
      if (isNative()) {
        const customerInfo = await purchaseMonthly();
        if (customerInfo) {
          await updateIsPremium(true);
          if (onJoin) onJoin();
          onClose();
        }
        setIsCheckoutLoading(false);
        return;
      }
      // Web: use Stripe
      const { data: { session } } = await supabase.auth.getSession();
      const authHeaders = session?.access_token
        ? { Authorization: `Bearer ${session.access_token}` }
        : {};
      const response = await axios.post(`${API}/create-checkout-session`, {
        origin_url: window.location.origin,
        user_email: session?.user?.email || ''
      }, { headers: authHeaders });
      window.location.href = response.data.url;
    } catch (err) {
      console.error('Checkout error:', err);
      setIsCheckoutLoading(false);
      setCheckoutError(true);
    }
  };
  
  return (
    <div className="paywall-overlay" data-testid="paywall-modal">
      <div className="paywall-modal">
        {/* Close button */}
        <button className="paywall-close" onClick={onClose} data-testid="paywall-close">
          <X size={24} />
        </button>
        
        {/* Logo */}
        <h1 className="paywall-logo">ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
        
        {/* Subtext */}
        <p className="paywall-subtext">
          ILIK is built independently by women, for women. Early supporters get a permanent rate that never increases.
        </p>
        
        {/* Feature list */}
        <ul className="paywall-features">
          <li><Check size={18} className="paywall-check" /> Plan all your months mapped in your seasons</li>
          <li><Check size={18} className="paywall-check" /> Invite a partner or friend to view your cycle</li>
          <li><Check size={18} className="paywall-check" /> Join a community of cycle-aware women</li>
          <li><Check size={18} className="paywall-check" /> First to be invited to ILIK workshops & retreats</li>
        </ul>
        
        {/* Price line */}
        <p className="paywall-price">€2.00/month<br/><span className="paywall-price-note">Early bird rate, yours forever</span></p>
        
        {/* CTA Button */}
        <button className="paywall-cta" onClick={handleJoin} disabled={isCheckoutLoading} data-testid="paywall-join">
          {isCheckoutLoading ? 'Redirecting...' : 'Get ILIK+ →'}
        </button>
        
        {checkoutError && (
          <p style={{ color: '#E53935', fontSize: '0.85rem', textAlign: 'center', margin: '0.5rem 0' }}>
            Something went wrong. Please try again.
          </p>
        )}
        
        {/* Cancel note */}
        <p className="paywall-cancel">Cancel anytime.</p>
      </div>
    </div>
  );
};

// Cycle Reflection Screen
const CycleReflectionScreen = ({ lastCycleData, onBeginNewCycle }) => {
  const daysWithEvents = lastCycleData?.events_count || 0;
  const cycleDays = lastCycleData?.days || [];
  
  return (
    <div className="reflection-screen" data-testid="reflection-screen">
      <div className="reflection-content">
        <span className="reflection-logo">ILIK</span>
        <h1 className="reflection-heading">Your last cycle</h1>
        
        <div className="cycle-strip">
          {cycleDays.map((day, index) => (
            <div 
              key={index} 
              className={`cycle-strip-day ${day.has_events ? 'has-events' : ''}`}
              style={{ backgroundColor: PHASE_DOT_COLORS[day.phase] + '40' }}
              title={`Day ${day.cycle_day} - ${day.phase}`}
            >
              {day.has_events && <span className="strip-dot"></span>}
            </div>
          ))}
        </div>
        
        <p className="reflection-count">
          {daysWithEvents > 0 
            ? `You showed up ${daysWithEvents} times last cycle.`
            : "Every cycle is a beginning. This one is yours."
          }
        </p>
        
        <p className="reflection-winter-message">
          Winter is here. Rest is not giving up. It is how you begin again.
        </p>
        
        <Button className="begin-cycle-btn" onClick={onBeginNewCycle} data-testid="begin-new-cycle-btn">
          Begin my new cycle
        </Button>
      </div>
    </div>
  );
};

// Today Screen - shown on app open before main interface
const TodayScreen = ({ currentCycle, settings, onEnter }) => {
  if (!currentCycle) return null;

  const phaseConfig = PHASE_CONFIG[currentCycle.phase];
  const cycleLength = settings?.cycle_length || 28;
  
  return (
    <div className="today-screen" data-testid="today-screen">
      {/* Phase color bar at top */}
      <div 
        className="today-phase-bar" 
        style={{ backgroundColor: phaseConfig.color }}
      />
      
      {/* Centered content */}
      <div className="today-content">
        <h1 
          className="today-season-name" 
          style={{ color: phaseConfig.color }}
          data-testid="today-season-name"
        >
          {currentCycle.phase.toUpperCase()}
        </h1>
        <p className="today-cycle-position" data-testid="today-cycle-position">
          Day {currentCycle.current_day} of {cycleLength}
        </p>
        <p className="today-interpretation" data-testid="today-interpretation">
          {currentCycle.period_expected
            ? 'Period expected, tap the drop when it arrives'
            : phaseConfig.tagline}
        </p>
      </div>
      
      {/* Enter button */}
      <button 
        className="today-enter-btn"
        onClick={onEnter}
        data-testid="today-enter-btn"
      >
        Enter my cycle →
      </button>
    </div>
  );
};

// Your Cycle Tab
const YourCycleTab = ({ currentCycle, calendarData, selectedMonth, setSelectedMonth, events, onAddEvent, onDeleteEvent, eventCounts, onSeasonClick }) => {
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayModal, setShowDayModal] = useState(false);
  const [showCapacityModal, setShowCapacityModal] = useState(false);
  const [capacityPhase, setCapacityPhase] = useState(null);
  const [bypassCapacity, setBypassCapacity] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showPaywallConfirm, setShowPaywallConfirm] = useState(false);
  const [monthMoods, setMonthMoods] = useState({});
  const [isPremium, setIsPremium] = useState(false);

  // Check premium status on mount
  useEffect(() => {
    checkIsPremium().then(result => setIsPremium(result));
  }, []);

  // Fetch moods whenever month changes
  useEffect(() => {
    const fetchMoods = async () => {
      const ym = `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`;
      const moods = await getMoodsForMonth(ym);
      setMonthMoods(moods);
    };
    fetchMoods();
  }, [selectedMonth]);

  const handleMoodChange = async (dateStr, emoji) => {
    const current = monthMoods[dateStr] || '';
    const arr = current ? current.split(',') : [];
    const idx = arr.indexOf(emoji);
    let updated;
    if (idx >= 0) {
      arr.splice(idx, 1);
      updated = arr.join(',');
    } else {
      if (arr.length >= 3) return;
      arr.push(emoji);
      updated = arr.join(',');
    }
    if (updated) {
      setMonthMoods(prev => ({ ...prev, [dateStr]: updated }));
      await upsertMood(dateStr, updated);
    } else {
      setMonthMoods(prev => { const next = { ...prev }; delete next[dateStr]; return next; });
      await deleteMood(dateStr);
    }
  };

  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() - 1);
    setSelectedMonth(newMonth);
  };

  const handleNextMonth = () => {
    const newMonth = new Date(selectedMonth);
    newMonth.setMonth(newMonth.getMonth() + 1);
    
    // Check if the target month is in the future (after current month)
    const now = new Date();
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    const targetMonthStart = new Date(newMonth.getFullYear(), newMonth.getMonth(), 1);
    
    if (targetMonthStart > currentMonthStart && !isPremium) {
      // Show paywall for future months (free users only)
      setShowPaywall(true);
    } else {
      setSelectedMonth(newMonth);
    }
  };

  const handlePaywallJoin = () => {
    setShowPaywall(false);
    setIsPremium(true);
    setShowPaywallConfirm(true);
    
    // Auto-dismiss confirmation after 3 seconds
    setTimeout(() => {
      setShowPaywallConfirm(false);
    }, 3000);
  };

  const handleDayClick = (dayData) => {
    if (dayData) {
      setSelectedDay(dayData);
      setShowDayModal(true);
    }
  };

  const handleShowCapacityMessage = (phase) => {
    setCapacityPhase(phase);
    setShowCapacityModal(true);
  };

  const getEventCountForDate = (dateStr) => {
    const countData = eventCounts.find(c => c.date === dateStr);
    return countData ? countData.count : 0;
  };

  const getDotSize = (count) => {
    if (count === 1) return 5;
    if (count === 2) return 4;
    return 3;
  };

  const monthNames = ["January", "February", "March", "April", "May", "June", 
                      "July", "August", "September", "October", "November", "December"];
  
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const renderCalendar = () => {
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const today = new Date().toISOString().split('T')[0];

    const days = [];
    
    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayData = calendarData.find(d => d.date === dateStr);
      const isToday = dateStr === today;
      const phaseColor = dayData ? PHASE_COLORS[dayData.phase] : "transparent";
      const eventCount = getEventCountForDate(dateStr);
      const dotColor = dayData ? PHASE_DOT_COLORS[dayData.phase] : "#999";
      const dotSize = getDotSize(eventCount);
      
      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? 'today' : ''}`}
          style={{ backgroundColor: phaseColor }}
          onClick={() => handleDayClick(dayData)}
          data-testid={`calendar-day-${dateStr}`}
        >
          <span className="day-number">{day}</span>
          {eventCount > 0 && (
            <div className="event-dots">
              {Array.from({ length: Math.min(eventCount, 7) }).map((_, i) => (
                <span 
                  key={i} 
                  className="event-dot-indicator" 
                  style={{ 
                    backgroundColor: dotColor,
                    width: `${dotSize}px`,
                    height: `${dotSize}px`
                  }}
                />
              ))}
            </div>
          )}
          {monthMoods[dateStr] && (
            <span className="mood-indicator" data-testid={`mood-indicator-${dateStr}`}>{monthMoods[dateStr].split(',')[0]}</span>
          )}
        </div>
      );
    }
    
    return days;
  };

  const phaseConfig = currentCycle ? PHASE_CONFIG[currentCycle.phase] : null;

  const NEXT_PHASE_COLORS = {
    Spring: '#43A047',
    Summer: '#FDD835',
    Autumn: '#FF6F00',
    Winter: '#E53935',
  };

  const getNextPhaseInfo = () => {
    if (!currentCycle) return null;
    const { current_day, cycle_length, period_length, phase } = currentCycle;
    if (!current_day || !cycle_length || !period_length || !phase) return null;

    const ovulationDay = cycle_length - 14;
    const phaseOrder = ['Winter', 'Spring', 'Summer', 'Autumn'];
    const nextPhase = phaseOrder[(phaseOrder.indexOf(phase) + 1) % 4];

    // First day of next phase (in cycle-day units), mirroring backend formula exactly:
    // Winter  <= period_length
    // Spring  <= ovulationDay - 2
    // Summer  <= ovulationDay + 2
    // Autumn  > ovulationDay + 2  (up to cycle_length)
    const nextPhaseStartDay = {
      Spring: period_length + 1,
      Summer: ovulationDay - 1,   // first day NOT in Spring
      Autumn: ovulationDay + 3,   // first day NOT in Summer
      Winter: cycle_length + 1,   // day 1 of next cycle
    }[nextPhase];

    const daysUntil = Math.max(0, nextPhaseStartDay - current_day);
    return { phase: nextPhase, daysUntil };
  };

  const nextPhaseInfo = getNextPhaseInfo();

  return (
    <div className="tab-content" data-testid="your-cycle-tab">
      {/* Current Season Card */}
      {currentCycle && phaseConfig && (
        <Card className="season-card" data-testid="current-season-card">
          <CardContent className="p-5">
            <div className="season-header-row">
              <span className="current-season-label">CURRENT SEASON</span>
              <button
                className="season-badge"
                style={{ backgroundColor: phaseConfig.bgColor, color: phaseConfig.color, border: 'none', cursor: 'pointer' }}
                data-testid="season-badge"
                onClick={() => onSeasonClick && onSeasonClick(currentCycle.phase)}
              >
                {currentCycle.phase === 'Spring' ? (
                  <>
                    <img src={SPRING_FLOWER_URL} alt="Spring" className="season-badge-flower" />
                    {currentCycle.phase}
                  </>
                ) : (
                  <>{phaseConfig.emoji} {currentCycle.phase}</>
                )}
              </button>
            </div>
            <div className="cycle-day-display">
              <span className="cycle-day-number" data-testid="cycle-day-number">{currentCycle.current_day}</span>
              <span className="cycle-day-label">Cycle Day</span>
            </div>
            {currentCycle.period_expected ? (
              <p className="period-status-text" data-testid="period-status-text" style={{ color: '#E53935' }}>
                Period expected, tap the <Droplet size={12} fill="#E53935" color="#E53935" style={{ display: 'inline', verticalAlign: 'middle' }} /> when it arrives
              </p>
            ) : nextPhaseInfo && (
              <p className="period-status-text" data-testid="period-status-text" style={{ color: NEXT_PHASE_COLORS[nextPhaseInfo.phase] }}>
                {nextPhaseInfo.daysUntil === 0
                  ? `Your ${nextPhaseInfo.phase} begins today`
                  : `Your ${nextPhaseInfo.phase} begins in ${nextPhaseInfo.daysUntil} ${nextPhaseInfo.daysUntil === 1 ? 'day' : 'days'}`}
              </p>
            )}
            <p className="season-tagline" data-testid="season-tagline">
              {phaseConfig.tagline}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Calendar */}
      <Card className="calendar-card" data-testid="calendar-card">
        <CardContent className="p-4">
          <div className="calendar-header">
            <Button variant="ghost" size="icon" onClick={handlePrevMonth} data-testid="prev-month-btn">
              <ChevronLeft size={20} />
            </Button>
            <h3 className="calendar-month" data-testid="calendar-month">
              {monthNames[selectedMonth.getMonth()]} {selectedMonth.getFullYear()}
            </h3>
            <Button variant="ghost" size="icon" onClick={handleNextMonth} data-testid="next-month-btn">
              <ChevronRight size={20} />
            </Button>
          </div>
          
          <div className="calendar-grid">
            {dayNames.map(day => (
              <div key={day} className="calendar-day-name">{day}</div>
            ))}
            {renderCalendar()}
          </div>

          {/* Season Legend */}
          <div className="season-legend" data-testid="season-legend">
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: PHASE_COLORS.Winter }}></span>
              <span className="legend-emoji">❄️</span>
              <span className="legend-text">Winter</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: PHASE_COLORS.Spring }}></span>
              <img src={SPRING_FLOWER_URL} alt="Spring" className="legend-spring-icon" />
              <span className="legend-text">Spring</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: PHASE_COLORS.Summer }}></span>
              <span className="legend-emoji">☀️</span>
              <span className="legend-text">Summer</span>
            </div>
            <div className="legend-item">
              <span className="legend-color" style={{ backgroundColor: PHASE_COLORS.Autumn }}></span>
              <span className="legend-emoji">🍂</span>
              <span className="legend-text">Autumn</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Day Detail Modal */}
      <DayDetailModal
        isOpen={showDayModal}
        onClose={() => { setShowDayModal(false); setBypassCapacity(false); }}
        dayData={selectedDay}
        events={events}
        onAddEvent={onAddEvent}
        onDeleteEvent={onDeleteEvent}
        onShowCapacityMessage={handleShowCapacityMessage}
        bypassCapacity={bypassCapacity}
        mood={selectedDay ? monthMoods[selectedDay.date] : null}
        onMoodChange={handleMoodChange}
      />

      {/* Capacity Limit Modal */}
      <CapacityModal
        isOpen={showCapacityModal}
        phase={capacityPhase}
        onClose={() => setShowCapacityModal(false)}
        onPlanAnyway={() => { setShowCapacityModal(false); setBypassCapacity(true); setShowDayModal(true); }}
      />

      {/* Premium Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        onJoin={handlePaywallJoin}
      />

      {/* Paywall Confirmation Toast */}
      {showPaywallConfirm && (
        <div className="paywall-confirm-toast" data-testid="paywall-confirm">
          <Check size={20} />
          <span>You're on the list. We'll notify you when ILIK Premium launches.</span>
        </div>
      )}
    </div>
  );
};

// Your Rhythm Tab (formerly Your Pattern)
const YourRhythmTab = ({ patternData, currentCycle, selectedSeason, setSelectedSeason, onBackFromEducation }) => {
  
  const cycleLength = patternData?.cycle_length || 28;
  const periodLength = patternData?.period_length || 5;
  const ovulationDay = cycleLength - 14;
  const currentDay = currentCycle?.current_day || 1;
  const currentPhase = currentCycle?.phase || "Winter";

  // Calculate phase lengths
  const winterDays = periodLength;
  const springDays = Math.max(0, (ovulationDay - 2) - periodLength);
  const summerDays = 5; // ovulation window is always 5 days
  const autumnDays = cycleLength - (ovulationDay + 2);

  // Calculate percentages
  const winterPercent = Math.round((winterDays / cycleLength) * 100);
  const springPercent = Math.round((springDays / cycleLength) * 100);
  const summerPercent = Math.round((summerDays / cycleLength) * 100);
  const autumnPercent = Math.round((autumnDays / cycleLength) * 100);

  // Calculate days until next season
  const getDaysUntilNextSeason = () => {
    if (currentPhase === "Winter") {
      return periodLength - currentDay + 1;
    } else if (currentPhase === "Spring") {
      const springEnd = ovulationDay - 2;
      const springStart = periodLength + 1;
      const dayInSpring = currentDay - springStart + 1;
      return springDays - dayInSpring + 1;
    } else if (currentPhase === "Summer") {
      const summerEnd = ovulationDay + 2;
      const summerStart = ovulationDay - 2;
      const dayInSummer = currentDay - summerStart + 1;
      return summerDays - dayInSummer + 1;
    } else {
      return cycleLength - currentDay + 1;
    }
  };

  const daysUntilNext = getDaysUntilNextSeason();

  // Get current phase emoji (returns 'spring-flower' for Spring to indicate image should be used)
  const getPhaseEmoji = () => {
    switch(currentPhase) {
      case "Winter": return "❄️";
      case "Spring": return "spring-flower";
      case "Summer": return "☀️";
      case "Autumn": return "🍂";
      default: return "❄️";
    }
  };

  // Get rhythm insight based on current phase
  const getRhythmInsight = () => {
    switch(currentPhase) {
      case "Winter":
        return "You are in your shortest season. These days ask the most of you quietly. Honour that.";
      case "Spring":
        return "Your energy is building. This is your best time to begin something you have been putting off.";
      case "Summer":
        return `You are in your peak window. Your Summer lasts ${summerDays} days, use them with intention.`;
      case "Autumn":
        return "Your longest season is winding down. The sensitivity you feel right now is not weakness, it is information.";
      default:
        return "";
    }
  };

  // Calculate arc positions for SVG
  const calculateArc = (startPercent, endPercent, isActive) => {
    const radius = isActive ? 115 : 110;
    const thickness = isActive ? 28 : 20;
    const innerRadius = radius - thickness;
    
    const startAngle = (startPercent / 100) * 360 - 90;
    const endAngle = (endPercent / 100) * 360 - 90;
    
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    const x1 = 130 + radius * Math.cos(startRad);
    const y1 = 130 + radius * Math.sin(startRad);
    const x2 = 130 + radius * Math.cos(endRad);
    const y2 = 130 + radius * Math.sin(endRad);
    const x3 = 130 + innerRadius * Math.cos(endRad);
    const y3 = 130 + innerRadius * Math.sin(endRad);
    const x4 = 130 + innerRadius * Math.cos(startRad);
    const y4 = 130 + innerRadius * Math.sin(startRad);
    
    const largeArc = (endPercent - startPercent) > 50 ? 1 : 0;
    
    return `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2} L ${x3} ${y3} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4} Z`;
  };

  // Calculate triangle arrow position on active arc
  const calculateArrowPosition = () => {
    let startPercent = 0;
    let phasePercent = 0;
    let dayInPhase = 0;
    let phaseDays = 0;

    if (currentPhase === "Winter") {
      startPercent = 0;
      phasePercent = winterPercent;
      dayInPhase = currentDay;
      phaseDays = winterDays;
    } else if (currentPhase === "Spring") {
      startPercent = winterPercent;
      phasePercent = springPercent;
      dayInPhase = currentDay - periodLength;
      phaseDays = springDays;
    } else if (currentPhase === "Summer") {
      startPercent = winterPercent + springPercent;
      phasePercent = summerPercent;
      dayInPhase = currentDay - (ovulationDay - 2) + 1;
      phaseDays = summerDays;
    } else {
      startPercent = winterPercent + springPercent + summerPercent;
      phasePercent = autumnPercent;
      dayInPhase = currentDay - (ovulationDay + 2);
      phaseDays = autumnDays;
    }

    const progressInPhase = phaseDays > 0 ? (dayInPhase - 0.5) / phaseDays : 0;
    // When period is expected but unconfirmed, cap the arrow at ~60% through
    // Autumn so it stays visually within the orange zone (the end of Autumn
    // borders Winter's red zone at the top of the circular wheel).
    const clampedProgress = currentCycle?.period_expected ? Math.min(progressInPhase, 0.6) : progressInPhase;
    const arrowPercent = startPercent + (phasePercent * clampedProgress);
    const arrowAngle = (arrowPercent / 100) * 360 - 90;
    const arrowRad = (arrowAngle * Math.PI) / 180;
    const arrowRadius = 123;
    
    return {
      x: 130 + arrowRadius * Math.cos(arrowRad),
      y: 130 + arrowRadius * Math.sin(arrowRad),
      angle: arrowAngle + 90 // Rotate to point inward
    };
  };

  // Calculate label positions
  const calculateLabelPosition = (startPercent, endPercent) => {
    const midPercent = (startPercent + endPercent) / 2;
    const angle = (midPercent / 100) * 360 - 90;
    const rad = (angle * Math.PI) / 180;
    const labelRadius = 138;
    return {
      x: 130 + labelRadius * Math.cos(rad),
      y: 130 + labelRadius * Math.sin(rad)
    };
  };

  const arrowPos = calculateArrowPosition();
  
  // Get current phase color for "You are here" label
  const currentPhaseColor = currentPhase === "Winter" ? "#E53935" : 
                            currentPhase === "Spring" ? "#43A047" : 
                            currentPhase === "Summer" ? "#FDD835" : "#FF6F00";

  // Arc segments with gap adjustment
  const gap = 1; // percent gap
  const winterStart = 0;
  const winterEnd = winterPercent - gap;
  const springStart = winterPercent + gap;
  const springEnd = winterPercent + springPercent - gap;
  const summerStart = winterPercent + springPercent + gap;
  const summerEnd = winterPercent + springPercent + summerPercent - gap;
  const autumnStart = winterPercent + springPercent + summerPercent + gap;
  const autumnEnd = 100 - gap;

  const seasonEducation = {
    Winter: {
      emoji: "❄️",
      name: "Winter",
      subtitle: "Period",
      days: `Day 1 – ${periodLength}`,
      color: "#E53935",
      bgColor: "rgba(229, 57, 53, 0.15)",
      gradient: "linear-gradient(180deg, #E53935 0%, #C62828 100%)",
      insideBody: "Your body is bleeding and releasing.",
      feelTags: ["Tired", "Tender", "Inward", "Quiet"],
      supportText: "This is your body asking for rest and warmth. Magnesium can help ease cramps and support sleep. Be gentle with your schedule and protect your energy without guilt.",
      closingLine: "Your period is not the end. It's the beginning."
    },
    Spring: {
      emoji: "spring-flower",
      name: "Spring",
      subtitle: "Follicular",
      days: `Day ${periodLength + 1} – ${ovulationDay - 2}`,
      color: "#43A047",
      bgColor: "rgba(67, 160, 71, 0.15)",
      gradient: "linear-gradient(180deg, #43A047 0%, #2E7D32 100%)",
      insideBody: "Estrogen rises. Your brain becomes more outward and curious.",
      feelTags: ["Optimistic", "Social", "Creative", "Energised"],
      supportText: "Your body is rebuilding and your energy is returning. Support that momentum with consistent sleep and time outside. This is a good phase to begin things you've been putting off.",
      closingLine: "Spring doesn't ask for permission to bloom. Neither do you."
    },
    Summer: {
      emoji: "☀️",
      name: "Summer",
      subtitle: "Ovulation",
      days: `Day ${ovulationDay - 2} – ${ovulationDay + 2}`,
      color: "#F9A825",
      bgColor: "rgba(253, 216, 53, 0.15)",
      gradient: "linear-gradient(180deg, #FDD835 0%, #F9A825 100%)",
      insideBody: "You are at your most outward and connected. Confidence comes naturally now.",
      feelTags: ["Confident", "Magnetic", "Warm", "Expressive"],
      supportText: "Your body is at its most open and energised. Support it by staying hydrated and getting quality sleep so your natural confidence and clarity can carry you through the day.",
      closingLine: "You are not too much in Summer. You are exactly enough."
    },
    Autumn: {
      emoji: "🍂",
      name: "Autumn",
      subtitle: "Luteal",
      days: `Day ${ovulationDay + 3} – until next period arrives`,
      color: "#FF6F00",
      bgColor: "rgba(255, 111, 0, 0.15)",
      gradient: "linear-gradient(180deg, #FF6F00 0%, #E65100 100%)",
      insideBody: "Progesterone rises. You become more inward, reflective and sensitive.",
      feelTags: ["Focused", "Emotional", "Sensitive", "Reflective"],
      supportText: "Your body is starting to wind down and your nervous system is more sensitive. Reducing caffeine and sugar can make a real difference to how you feel. Magnesium and rest are your best support right now.",
      closingLine: "Autumn is not a bad phase. It's an honest one."
    }
  };

  if (selectedSeason) {
    const season = seasonEducation[selectedSeason];
    return (
      <div className="season-education-screen" data-testid={`education-${selectedSeason.toLowerCase()}`}>
        {/* Illustrated Header */}
        <div className={`education-header education-header-${selectedSeason.toLowerCase()}`}>
          <button className="back-btn" onClick={() => onBackFromEducation ? onBackFromEducation() : setSelectedSeason(null)} data-testid="back-from-education">
            <ChevronLeft size={24} />
          </button>
          
          {/* Background Elements */}
          <div className="header-bg-elements">
            {selectedSeason === 'Winter' && (
              <>
                <div className="snowflake sf-1">❄</div>
                <div className="snowflake sf-2">❄</div>
                <div className="snowflake sf-3">❄</div>
                <div className="snowflake sf-4">❄</div>
                <div className="snowflake sf-5">❄</div>
                <div className="snowflake sf-6">❄</div>
              </>
            )}
            {selectedSeason === 'Spring' && (
              <>
                {/* Spring: sparkle animation like Summer */}
                <div className="sparkle sp-1">✨</div>
                <div className="sparkle sp-2">✨</div>
                <div className="sparkle sp-3">✨</div>
                <div className="sparkle sp-4">✨</div>
                <div className="sparkle sp-5">✨</div>
                <div className="sparkle sp-6">✨</div>
                <div className="sparkle sp-7">✨</div>
              </>
            )}
            {selectedSeason === 'Summer' && (
              <>
                <div className="sun-ray ray-1"></div>
                <div className="sun-ray ray-2"></div>
                <div className="sun-ray ray-3"></div>
                <div className="sun-ray ray-4"></div>
                <div className="sun-ray ray-5"></div>
                <div className="sun-ray ray-6"></div>
              </>
            )}
            {selectedSeason === 'Autumn' && (
              <>
                <div className="leaf lf-1">🍂</div>
                <div className="leaf lf-2">🍁</div>
                <div className="leaf lf-3">🍂</div>
                <div className="leaf lf-4">🍁</div>
                <div className="leaf lf-5">🍂</div>
              </>
            )}
          </div>

          {/* Center Content */}
          <div className="header-center-content">
            {selectedSeason === 'Spring' ? (
              <img 
                src={SPRING_FLOWER_URL} 
                alt="Cherry blossom" 
                className="spring-blossom-icon"
              />
            ) : (
              <span className="header-main-emoji">{season.emoji}</span>
            )}
            <h1 className="header-season-name">{season.name}</h1>
            <p className="header-phase-name">{season.subtitle === 'Period' ? <><Droplet size={12} fill="#E53935" color="#E53935" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 3 }} />{season.subtitle}</> : season.subtitle} Phase</p>
            <p className="header-days">{season.days}</p>
          </div>
        </div>

        {/* Content sections */}
        <div className="education-cards">
          {/* Section 1: Anchor phrase - same as Home screen interpretation */}
          <div className="education-anchor-section">
            <p className="anchor-phrase" style={{ color: season.color }}>
              {PHASE_CONFIG[selectedSeason].tagline}
            </p>
          </div>

          {/* Section 2: During this season */}
          <div className="education-card">
            <h3 className="card-label">During this season</h3>
            <p className="card-body-text">{season.supportText}</p>
          </div>
        </div>

        {/* Footer line - appears on all season cards */}
        <div className="education-footer">
          <p className="footer-notice">Cycle patterns vary. Notice yours.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="tab-content" data-testid="your-rhythm-tab">
      <h2 className="rhythm-title">Your Rhythm</h2>
      
      {/* Phase Circle Visualization */}
      <div className="phase-circle-container">
        <svg viewBox="-40 -40 340 340" className="phase-circle-svg">
          {/* Winter Arc */}
          <path 
            d={calculateArc(winterStart, winterEnd, currentPhase === "Winter")} 
            fill="#E53935"
          />
          {/* Spring Arc */}
          <path 
            d={calculateArc(springStart, springEnd, currentPhase === "Spring")} 
            fill="#43A047"
          />
          {/* Summer Arc */}
          <path 
            d={calculateArc(summerStart, summerEnd, currentPhase === "Summer")} 
            fill="#FDD835"
          />
          {/* Autumn Arc */}
          <path 
            d={calculateArc(autumnStart, autumnEnd, currentPhase === "Autumn")} 
            fill="#FF6F00"
          />
          
          {/* Triangle Arrow Marker - matches current phase color */}
          <g transform={`translate(${arrowPos.x}, ${arrowPos.y}) rotate(${arrowPos.angle})`}>
            <polygon 
              points="0,-6 5,4 -5,4" 
              fill={currentPhaseColor}
              className="phase-arrow-pulse"
            />
          </g>
        </svg>
        
        {/* Day Labels - positioned at R + 40px from center at segment midpoint angles */}
        {(() => {
          const R = 120; // outer radius of donut
          const labelDistance = R + 45; // 45px outside the ring for better spacing
          
          // Gap adjustment (1% gap between segments)
          const gap = 1;
          
          // Calculate midpoint angles for each segment (in degrees, starting from -90 for top)
          // Account for gaps between segments
          const winterMidAngle = (winterPercent / 2) / 100 * 360 - 90;
          const springMidAngle = (winterPercent + gap + springPercent / 2) / 100 * 360 - 90;
          const summerMidAngle = (winterPercent + springPercent + gap * 2 + summerPercent / 2) / 100 * 360 - 90;
          const autumnMidAngle = (winterPercent + springPercent + summerPercent + gap * 3 + autumnPercent / 2) / 100 * 360 - 90;
          
          // Convert to radians and calculate positions as percentages of container
          const toRad = (deg) => deg * Math.PI / 180;
          const containerSize = 340;
          const centerPct = 50;
          const distancePct = (labelDistance / containerSize) * 100;
          
          return (
            <>
              {/* Winter Label */}
              <div className="phase-day-label-container" style={{
                left: `${centerPct + distancePct * Math.cos(toRad(winterMidAngle))}%`,
                top: `${centerPct + distancePct * Math.sin(toRad(winterMidAngle))}%`
              }}>
                <span className="phase-day-number" style={{ color: '#E53935' }}>{winterDays}</span>
                <span className="phase-day-text">days</span>
              </div>
              
              {/* Spring Label */}
              <div className="phase-day-label-container" style={{
                left: `${centerPct + distancePct * Math.cos(toRad(springMidAngle))}%`,
                top: `${centerPct + distancePct * Math.sin(toRad(springMidAngle))}%`
              }}>
                <span className="phase-day-number" style={{ color: '#43A047' }}>{springDays}</span>
                <span className="phase-day-text">days</span>
              </div>
              
              {/* Summer Label - using #F9A825 for visibility */}
              <div className="phase-day-label-container" style={{
                left: `${centerPct + distancePct * Math.cos(toRad(summerMidAngle))}%`,
                top: `${centerPct + distancePct * Math.sin(toRad(summerMidAngle))}%`
              }}>
                <span className="phase-day-number" style={{ color: '#F9A825' }}>{summerDays}</span>
                <span className="phase-day-text">days</span>
              </div>
              
              {/* Autumn Label */}
              <div className="phase-day-label-container" style={{
                left: `${centerPct + distancePct * Math.cos(toRad(autumnMidAngle))}%`,
                top: `${centerPct + distancePct * Math.sin(toRad(autumnMidAngle))}%`
              }}>
                <span className="phase-day-number" style={{ color: '#FF6F00' }}>{autumnDays}</span>
                <span className="phase-day-text">days</span>
              </div>
            </>
          );
        })()}
        
        {/* Center Content */}
        <div className="phase-circle-center">
          <Droplet size={24} className="center-blood-drop" fill="#E53935" color="#E53935" />
          <span className="center-days">{cycleLength}</span>
          <span className="center-label">days</span>
        </div>
      </div>
      
      {/* Day Progress Text */}
      <p className="rhythm-progress">
        Day {currentDay} of {cycleLength}<br />
        {currentCycle?.period_expected
          ? 'Period expected, tap the drop when it arrives'
          : `${daysUntilNext} days until your next season`}
      </p>

      {/* Four Season Education Cards */}
      <div className="rhythm-seasons">
        <h4 className="seasons-header">Your 4 Seasons</h4>
        <div className="phase-info-list">
          {Object.entries(seasonEducation).map(([key, season]) => (
            <button
              key={key}
              className="phase-info-item clickable"
              style={{ backgroundColor: season.bgColor }}
              onClick={() => setSelectedSeason(key)}
              data-testid={`season-card-${key.toLowerCase()}`}
            >
              {key === 'Spring' ? (
                <img 
                  src={SPRING_FLOWER_URL} 
                  alt="Spring" 
                  className="phase-icon-img"
                />
              ) : (
                <span className="phase-icon">{season.emoji}</span>
              )}
              <div className="phase-details">
                <span className="phase-name" style={{ color: season.color }}>{season.name} ({season.subtitle === 'Period' ? <><Droplet size={11} fill="#E53935" color="#E53935" style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />{season.subtitle}</> : season.subtitle})</span>
                <span className="phase-days">{season.days}</span>
              </div>
              <ChevronRight size={20} className="phase-arrow" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

// Settings Tab
// Legal Page — reusable full-screen component
const LegalPage = ({ title, onBack, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'white', zIndex: 4000, overflowY: 'auto', fontFamily: "'Nunito', sans-serif" }} data-testid="legal-page">
    <div style={{ maxWidth: 480, margin: '0 auto', padding: '0 24px 60px' }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '20px 0 8px', gap: 12 }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#757575', fontSize: '1.1rem', padding: '4px 0', display: 'flex', alignItems: 'center', gap: 4 }} data-testid="legal-back-btn">
          <ChevronLeft size={20} /> Back
        </button>
      </div>
      <div style={{ textAlign: 'center', padding: '12px 0 28px' }}>
        <h1 className="hero-wordmark-gradient" style={{ marginBottom: '0.1rem' }}>ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', margin: 0 }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
      </div>
      <h2 style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1.1rem', color: '#212121', marginBottom: 20 }}>{title}</h2>
      <div style={{ fontSize: '0.875rem', color: '#212121', lineHeight: 1.75 }}>{children}</div>
    </div>
  </div>
);

const PrivacyPolicyPage = ({ onBack }) => {
  const tableStyle = { width: '100%', borderCollapse: 'collapse', marginBottom: 16, fontSize: '0.8rem' };
  const thStyle = { border: '1px solid #ddd', padding: '8px 10px', background: '#f5f5f5', textAlign: 'left', fontWeight: 700 };
  const tdStyle = { border: '1px solid #ddd', padding: '8px 10px', verticalAlign: 'top' };
  return (
  <LegalPage title="PRIVACY POLICY" onBack={onBack}>
    <p style={{ color: '#757575', marginBottom: 4 }}>Version 1.0</p>
    <p style={{ color: '#757575', marginBottom: 20 }}>Effective Date: 17 April 2026</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>1. Introduction</p>
    <p style={{ marginBottom: 8 }}>1.1 ILIK B.V. ("we", "us", "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, store, share, and protect your personal data when you use the ILIK mobile application, the website at ilikapp.com, and associated services (collectively, the "Services").</p>
    <p style={{ marginBottom: 8 }}>1.2 This policy also informs you about your rights under European Union data protection law, including Regulation (EU) 2016/679 (the "GDPR") and the Dutch Implementation Act (Uitvoeringswet Algemene verordening gegevensbescherming, the "UAVG").</p>
    <p style={{ marginBottom: 20 }}>1.3 This policy applies to all personal data processed by ILIK B.V. in relation to the provision of the Services.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>2. Data Controller Information</p>
    <p style={{ marginBottom: 8 }}>2.1 ILIK B.V. is the data controller responsible for your personal data.</p>
    <p style={{ marginBottom: 8 }}>2.2 Our registered address is held with the Netherlands Chamber of Commerce (Kamer van Koophandel) and is disclosed in our trader information on the App Store under the EU Digital Services Act. It is available on request to support@ilikapp.com.</p>
    <p style={{ marginBottom: 20 }}>2.3 We have not appointed a Data Protection Officer, as the Services do not meet the thresholds for mandatory appointment under Article 37 GDPR. Privacy matters are handled directly by the company. If you have any questions about this policy or our privacy practices, please contact us at support@ilikapp.com.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>3. Personal Data We Collect</p>
    <p style={{ marginBottom: 8 }}>We collect personal data that you provide directly and data generated when you use the Services. We may collect and process the following categories of personal data about you:</p>
    <p style={{ marginBottom: 8 }}><strong>Contact Data:</strong> your email address.</p>
    <p style={{ marginBottom: 8 }}><strong>Authentication Data:</strong> your password, stored in a securely hashed form that cannot be reversed.</p>
    <p style={{ marginBottom: 8 }}><strong>Health and Profile Data (Special Category Data):</strong> information about your menstrual cycle, including period start and end dates, typical cycle length, and typical period length. Where you choose to provide it, information about your contraception.</p>
    <p style={{ marginBottom: 8 }}><strong>Event Data:</strong> details of the events you create within the Services, including titles, dates, and times.</p>
    <p style={{ marginBottom: 8 }}><strong>Subscription Data:</strong> whether your account has an active ILIK+ subscription, the date it expires, and the payment platform through which it was activated.</p>
    <p style={{ marginBottom: 8 }}><strong>Partner Sharing Data (ILIK+ only):</strong> an anonymous sharing token generated when you enable the Share with Partner feature.</p>
    <p style={{ marginBottom: 8 }}><strong>Device Identifier (if you enable notifications):</strong> a token provided by your device operating system that allows your device to receive notifications from us.</p>
    <p style={{ marginBottom: 20 }}><strong>Technical Data:</strong> basic technical information required for the operation of a secure online service, including IP address, request timestamps, session authentication state, and device type for debugging and service optimisation.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>4. How We Use Your Personal Data and Lawful Bases</p>
    <p style={{ marginBottom: 8 }}>We will only use your personal data when the law allows us to. Most commonly, we will use your personal data in the following circumstances and on the following lawful bases:</p>
    <div style={{ overflowX: 'auto', marginBottom: 20 }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Category of personal data</th>
            <th style={thStyle}>Purpose of processing</th>
            <th style={thStyle}>Lawful basis under GDPR Article 6</th>
            <th style={thStyle}>Specific condition for Special Category Data (GDPR Article 9)</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style={tdStyle}>Email address, password</td><td style={tdStyle}>Account creation, authentication, and security</td><td style={tdStyle}>Performance of contract (Article 6(1)(b))</td><td style={tdStyle}>Not applicable</td></tr>
          <tr><td style={tdStyle}>Menstrual cycle information</td><td style={tdStyle}>To provide the core functionality of the Services, including phase calculation and the seasonal calendar</td><td style={tdStyle}>Explicit consent (Article 6(1)(a))</td><td style={tdStyle}>Explicit consent (Article 9(2)(a))</td></tr>
          <tr><td style={tdStyle}>Contraception information (if provided)</td><td style={tdStyle}>To adapt the app's interpretations to your situation</td><td style={tdStyle}>Explicit consent (Article 6(1)(a))</td><td style={tdStyle}>Explicit consent (Article 9(2)(a))</td></tr>
          <tr><td style={tdStyle}>Event data</td><td style={tdStyle}>To display your personal calendar within the Services</td><td style={tdStyle}>Consent (Article 6(1)(a))</td><td style={tdStyle}>Not applicable</td></tr>
          <tr><td style={tdStyle}>Partner sharing token (ILIK+)</td><td style={tdStyle}>To enable the Share with Partner feature</td><td style={tdStyle}>Consent (Article 6(1)(a))</td><td style={tdStyle}>Not applicable</td></tr>
          <tr><td style={tdStyle}>Subscription data</td><td style={tdStyle}>To provide access to ILIK+ features and manage billing</td><td style={tdStyle}>Performance of contract (Article 6(1)(b))</td><td style={tdStyle}>Not applicable</td></tr>
          <tr><td style={tdStyle}>Device identifier</td><td style={tdStyle}>To deliver notifications you have requested</td><td style={tdStyle}>Consent (Article 6(1)(a))</td><td style={tdStyle}>Not applicable</td></tr>
          <tr><td style={tdStyle}>Technical data</td><td style={tdStyle}>To monitor service usage, troubleshoot issues, prevent abuse, and improve service performance</td><td style={tdStyle}>Legitimate interests (Article 6(1)(f)), for service security and improvement</td><td style={tdStyle}>Not applicable</td></tr>
          <tr><td style={tdStyle}>Email address</td><td style={tdStyle}>To communicate important service updates or respond to your enquiries</td><td style={tdStyle}>Legitimate interests (Article 6(1)(f)), for service communication</td><td style={tdStyle}>Not applicable</td></tr>
        </tbody>
      </table>
    </div>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>5. Special Category Data</p>
    <p style={{ marginBottom: 8 }}>5.1 Data concerning your menstrual cycle, and where you choose to provide it your contraception information, is considered "special category data" under Article 9 GDPR, specifically as data concerning health.</p>
    <p style={{ marginBottom: 8 }}>5.2 We process this data solely to provide the core features of the Services. We do not use it for advertising, we do not share it for marketing, and we do not combine it with data from other sources for any purpose other than delivering the Services to you.</p>
    <p style={{ marginBottom: 8 }}>5.3 We rely on your explicit consent (Article 9(2)(a) GDPR) as the lawful condition for processing this special category data. You provide this consent during onboarding and whenever you add new cycle information.</p>
    <p style={{ marginBottom: 20 }}>5.4 You have the right to withdraw your consent for the processing of your special category data at any time. Please note that withdrawing consent will result in you being unable to use the Services, as cycle information is fundamental to their operation. To withdraw consent, you may delete your account from within the app or contact support@ilikapp.com.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>6. What We Do Not Collect</p>
    <p style={{ marginBottom: 8 }}>We want to be explicit about this. The following categories of data are not collected by the Services:</p>
    <p style={{ marginBottom: 8 }}>6.1 We do not collect your location data, GPS coordinates, or precise whereabouts.</p>
    <p style={{ marginBottom: 8 }}>6.2 We do not access your contacts, address book, photos, camera, or microphone.</p>
    <p style={{ marginBottom: 8 }}>6.3 We do not import data from Apple Health, Google Fit, or any other third-party health platform.</p>
    <p style={{ marginBottom: 8 }}>6.4 We do not collect your social media profiles or data from third-party social accounts.</p>
    <p style={{ marginBottom: 8 }}>6.5 We do not store your payment card details, bank details, or full billing address. These are processed exclusively by the payment service providers listed in section 8.</p>
    <p style={{ marginBottom: 20 }}>6.6 We do not collect biometric data.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>7. Data Retention</p>
    <p style={{ marginBottom: 8 }}>7.1 We will retain your personal data only for as long as necessary to fulfil the purposes for which we collected it, including for the purposes of satisfying any legal, accounting, or reporting requirements.</p>
    <p style={{ marginBottom: 8 }}>7.2 We will generally retain your personal data for as long as you maintain an active account with us.</p>
    <p style={{ marginBottom: 8 }}>7.3 When you delete your account, either from within the app or by contacting support@ilikapp.com, all your personal data including cycle information, events, preferences, and partner tokens will be permanently deleted within 30 days of your request. This is in line with Article 17 GDPR.</p>
    <p style={{ marginBottom: 8 }}>7.4 Following closure of your account, we may retain certain records relating to ILIK+ subscription transactions for up to seven (7) years, where required by Dutch tax and accounting law (Algemene wet inzake rijksbelastingen, article 52). This retention is limited to what is strictly necessary to comply with legal obligations and does not include your cycle data or other product data.</p>
    <p style={{ marginBottom: 20 }}>7.5 After the expiry of the applicable retention period, we will securely delete or irreversibly anonymise your personal data.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>8. Data Sharing and Subprocessors</p>
    <p style={{ marginBottom: 8 }}>8.1 We do not sell your personal data. We may share your personal data with trusted third-party service providers who act as data processors on our behalf ("Subprocessors") to provide specific services. These Subprocessors are contractually bound to process your data only under our instructions and to implement appropriate security measures.</p>
    <p style={{ marginBottom: 8 }}>8.2 Our current Subprocessors include:</p>
    <div style={{ overflowX: 'auto', marginBottom: 8 }}>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Subprocessor</th>
            <th style={thStyle}>Purpose</th>
            <th style={thStyle}>Location</th>
          </tr>
        </thead>
        <tbody>
          <tr><td style={tdStyle}>Supabase</td><td style={tdStyle}>Database hosting and authentication</td><td style={tdStyle}>European Union (Germany)</td></tr>
          <tr><td style={tdStyle}>Emergent</td><td style={tdStyle}>Application hosting</td><td style={tdStyle}>United States</td></tr>
          <tr><td style={tdStyle}>Vercel</td><td style={tdStyle}>Website and content delivery</td><td style={tdStyle}>Global with European Union edge</td></tr>
          <tr><td style={tdStyle}>Stripe</td><td style={tdStyle}>Web payment processing</td><td style={tdStyle}>European Union and United States</td></tr>
          <tr><td style={tdStyle}>Apple</td><td style={tdStyle}>iOS subscription payment processing</td><td style={tdStyle}>United States</td></tr>
          <tr><td style={tdStyle}>RevenueCat</td><td style={tdStyle}>iOS subscription management</td><td style={tdStyle}>United States</td></tr>
          <tr><td style={tdStyle}>Google Workspace</td><td style={tdStyle}>Transactional email</td><td style={tdStyle}>European Union and United States</td></tr>
        </tbody>
      </table>
    </div>
    <p style={{ marginBottom: 8 }}>8.3 The categories of personal data that leave our primary database are limited. In particular, your menstrual cycle information, event data, and preferences are stored within the European Union and are not transferred to Subprocessors located outside the European Economic Area.</p>
    <p style={{ marginBottom: 8 }}>8.4 Payment service providers (Stripe, Apple, RevenueCat) process transaction data under their own privacy terms, which are available on their respective websites. We receive from them only the minimum data necessary to recognise which of our users has an active subscription.</p>
    <p style={{ marginBottom: 20 }}>8.5 We may disclose your personal data if required by law, regulation, or legal process, or to protect the rights, property, or safety of ILIK B.V., our users, or others. Where legally permitted, we will notify you of any such disclosure.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>9. International Data Transfers</p>
    <p style={{ marginBottom: 8 }}>9.1 Your most sensitive personal data, including your cycle information, events, and account data, is stored within the European Union. This data does not leave the European Economic Area for storage.</p>
    <p style={{ marginBottom: 8 }}>9.2 Some of our Subprocessors are located outside the European Economic Area, primarily in the United States. When we transfer your personal data to Subprocessors located outside the EEA in countries not covered by an adequacy decision of the European Commission, we ensure appropriate safeguards are in place as required by Chapter V of the GDPR.</p>
    <p style={{ marginBottom: 20 }}>9.3 We primarily rely on the Standard Contractual Clauses adopted by the European Commission under Article 46 GDPR, supplemented by appropriate technical and organisational measures where necessary, to ensure your data receives a level of protection equivalent to that provided within the European Union.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>10. Data Security</p>
    <p style={{ marginBottom: 8 }}>10.1 We have implemented appropriate technical and organisational security measures to prevent your personal data from being accidentally lost, used, accessed in an unauthorised way, altered, or disclosed. These measures include encryption in transit using industry-standard protocols, encryption at rest of our primary database, secure one-way hashing of passwords, and access controls that enforce the principle that each user can only access their own data.</p>
    <p style={{ marginBottom: 8 }}>10.2 Access to your personal data is limited to the small number of persons who have a legitimate operational need for access. They will only process your personal data in accordance with documented instructions, and they are subject to a duty of confidentiality.</p>
    <p style={{ marginBottom: 20 }}>10.3 We have procedures in place to deal with any suspected personal data breach. Should a personal data breach occur that poses a risk to your rights and freedoms, we will notify the Autoriteit Persoonsgegevens within 72 hours of becoming aware of the breach, and notify affected users without undue delay, in accordance with Articles 33 and 34 GDPR.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>11. Your Data Protection Rights</p>
    <p style={{ marginBottom: 8 }}>Under the GDPR, you have the following rights in relation to your personal data:</p>
    <p style={{ marginBottom: 8 }}><strong>Right of access</strong> (Article 15): to request copies of your personal data.</p>
    <p style={{ marginBottom: 8 }}><strong>Right to rectification</strong> (Article 16): to request correction of inaccurate personal data.</p>
    <p style={{ marginBottom: 8 }}><strong>Right to erasure</strong> (Article 17, the "right to be forgotten"): to request deletion of your personal data where there is no compelling reason for its continued processing.</p>
    <p style={{ marginBottom: 8 }}><strong>Right to restrict processing</strong> (Article 18): to request the suspension of processing under certain conditions.</p>
    <p style={{ marginBottom: 8 }}><strong>Right to data portability</strong> (Article 20): to request the transfer of your personal data to you or a third party in a structured, commonly used, machine-readable format. An export in JSON format is available from within the app.</p>
    <p style={{ marginBottom: 8 }}><strong>Right to object</strong> (Article 21): to object to processing based on our legitimate interests.</p>
    <p style={{ marginBottom: 8 }}><strong>Right to withdraw consent</strong> (Article 7(3)): to withdraw your consent at any time where we rely on consent to process your data, including explicit consent for special category data.</p>
    <p style={{ marginBottom: 20 }}>To exercise any of these rights, please contact us at support@ilikapp.com. We may need to request specific information from you to help us confirm your identity. Exercising these rights is free of charge and does not require you to provide a reason.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>12. Automated Processing</p>
    <p style={{ marginBottom: 8 }}>12.1 ILIK uses mathematical calculations to predict your cycle phases from the data you provide. This is a purely deterministic calculation based on your input.</p>
    <p style={{ marginBottom: 8 }}>12.2 The Services do not involve profiling, machine learning, or any form of automated decision-making that produces legal or similarly significant effects on you within the meaning of Article 22 GDPR.</p>
    <p style={{ marginBottom: 20 }}>12.3 The predictions produced by the Services are for informational purposes only. They are not medical advice, diagnosis, or treatment. The Services are not a medical device.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>13. Children's Privacy</p>
    <p style={{ marginBottom: 8 }}>13.1 The Services are intended exclusively for users aged eighteen (18) years or older. This minimum age is both a contractual requirement under our Terms of Use and the basis on which we process your personal data.</p>
    <p style={{ marginBottom: 8 }}>13.2 We do not knowingly collect personal data from individuals under eighteen (18). If we become aware that a user is under eighteen, we will delete their account and all associated data as soon as practicable.</p>
    <p style={{ marginBottom: 20 }}>13.3 If you believe a minor has created an account, please contact support@ilikapp.com.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>14. Partner Sharing Feature</p>
    <p style={{ marginBottom: 8 }}>14.1 If you enable the Share with Partner feature, available to ILIK+ subscribers only, a unique anonymous token is generated. Anyone with the corresponding link can view a read-only summary of your current cycle phase and the interpretation cards associated with it.</p>
    <p style={{ marginBottom: 8 }}>14.2 The partner view does not display your name, email, events, preferences, or any other identifying information. It shows only the phase summary.</p>
    <p style={{ marginBottom: 8 }}>14.3 You control this link. You may revoke it at any time by deleting your account, at which point all partner tokens associated with your account are permanently invalidated.</p>
    <p style={{ marginBottom: 20 }}>14.4 By generating and sharing a partner link, you take responsibility for the decision to share this information with the specific person you share it with.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>15. Changes to This Privacy Policy</p>
    <p style={{ marginBottom: 8 }}>15.1 We may update this Privacy Policy from time to time. Any changes will be posted on this page with an updated effective date.</p>
    <p style={{ marginBottom: 8 }}>15.2 We will notify you of any material changes to this policy through the Services by in-app notification or by email, where appropriate. We encourage you to review this policy periodically.</p>
    <p style={{ marginBottom: 20 }}>15.3 Continued use of the Services after the effective date of a revised policy constitutes acceptance of the updated terms. If you do not agree with a change, you may delete your account before it takes effect.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>16. How to Contact Us and Complaints</p>
    <p style={{ marginBottom: 8 }}>16.1 If you have any questions or concerns about this Privacy Policy or our data protection practices, please contact us at support@ilikapp.com.</p>
    <p style={{ marginBottom: 8 }}>16.2 You also have the right to lodge a complaint at any time with the Autoriteit Persoonsgegevens, the Dutch supervisory authority for data protection matters.</p>
    <p>16.3 We would, however, appreciate the chance to deal with your concerns before you approach the supervisory authority. Please contact us at support@ilikapp.com in the first instance.</p>
  </LegalPage>
  );
};

const TermsOfUsePage = ({ onBack }) => (
  <LegalPage title="TERMS OF USE" onBack={onBack}>
    <p style={{ color: '#757575', marginBottom: 4 }}>Version 1.0</p>
    <p style={{ color: '#757575', marginBottom: 20 }}>Effective Date: 17 April 2026</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>1. Introduction</p>
    <p style={{ marginBottom: 8 }}>1.1 These Terms of Use govern your use of the ILIK mobile application, the website at ilikapp.com, and all associated services (collectively, the "Services") provided by ILIK B.V., a private limited company registered in the Netherlands.</p>
    <p style={{ marginBottom: 8 }}>1.2 By accessing or using any part of the Services, you agree to be legally bound by these Terms. If you do not accept these Terms, you must not use the Services.</p>
    <p style={{ marginBottom: 20 }}>1.3 These Terms apply to all users of the Services, including account holders and visitors to ilikapp.com.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>2. Eligibility</p>
    <p style={{ marginBottom: 8 }}>2.1 The Services are intended exclusively for users aged eighteen (18) years or older. By using the Services, you represent and warrant that you are at least 18 years old.</p>
    <p style={{ marginBottom: 8 }}>2.2 We do not knowingly collect or process personal data from individuals under 18. If we become aware that a user is under 18, we will delete their account and all associated data as soon as practicable.</p>
    <p style={{ marginBottom: 20 }}>2.3 By creating an account, you confirm you meet the eligibility requirements set out in this section.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>3. Account Registration and Security</p>
    <p style={{ marginBottom: 8 }}>3.1 To use the Services, you must register for an account. During registration you will be asked to provide a valid email address, a password, and a name or display name for personalisation purposes. You agree to provide accurate, current, and complete information.</p>
    <p style={{ marginBottom: 8 }}>3.2 Authentication is handled securely, with your password stored in a form that cannot be reversed. You are responsible for maintaining the confidentiality of your account credentials.</p>
    <p style={{ marginBottom: 8 }}>3.3 You are responsible for all activity that occurs under your account. You agree to notify us immediately of any unauthorised access or use by contacting support@ilikapp.com.</p>
    <p style={{ marginBottom: 20 }}>3.4 You may not share your account credentials with any other person. Each account is for a single individual user only.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>4. Permitted Use</p>
    <p style={{ marginBottom: 8 }}>4.1 You may use the Services solely for your personal, non-commercial use and in accordance with these Terms and all applicable laws and regulations.</p>
    <p style={{ marginBottom: 8 }}>4.2 You agree not to:</p>
    <ul style={{ paddingLeft: 20, marginBottom: 20 }}>
      <li style={{ marginBottom: 4 }}>Copy, reproduce, modify, distribute, or create derivative works based on the Services or any part thereof;</li>
      <li style={{ marginBottom: 4 }}>Reverse engineer, decompile, or disassemble any part of the Services;</li>
      <li style={{ marginBottom: 4 }}>Use the Services for any unlawful, harmful, or fraudulent purpose;</li>
      <li style={{ marginBottom: 4 }}>Use automated systems, bots, or scrapers to access the Services;</li>
      <li style={{ marginBottom: 4 }}>Attempt to gain unauthorised access to any part of the Services or their infrastructure;</li>
      <li style={{ marginBottom: 4 }}>Use the Services to develop a competing product or service;</li>
      <li style={{ marginBottom: 4 }}>Transmit harmful code, viruses, or malicious content through the Services.</li>
    </ul>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>5. Intellectual Property</p>
    <p style={{ marginBottom: 8 }}>5.1 All intellectual property rights in the Services, including all software, content, design elements, the 4 Seasons of You framework, and the ILIK name and brand, are owned by or licensed to ILIK B.V. All rights are reserved.</p>
    <p style={{ marginBottom: 8 }}>5.2 You are granted a limited, non-exclusive, non-transferable licence to use the Services solely for your personal use in accordance with these Terms. This licence does not include any right to sublicense, sell, or commercially exploit the Services.</p>
    <p style={{ marginBottom: 20 }}>5.3 The 4 Seasons of You<sup style={{fontSize: '0.5em'}}>&#174;</sup> framework is a registered proprietary to ILIK. You may not reproduce, publish or distribute this framework or any part of it without prior written consent.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>6. Your Data</p>
    <p style={{ marginBottom: 8 }}>6.1 You retain ownership of the data you enter into the Services, including your cycle information, events, and preferences.</p>
    <p style={{ marginBottom: 8 }}>6.2 By using the Services, you grant us a limited licence to process that data solely to provide the Services to you. We make no claim of ownership over your personal data.</p>
    <p style={{ marginBottom: 20 }}>6.3 How we process your personal data is described in our Privacy Policy, available at ilikapp.com/privacy. The Privacy Policy forms part of these Terms.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>7. Medical Disclaimer</p>
    <p style={{ marginBottom: 8, fontWeight: 600, textTransform: 'uppercase' }}>Important notice: ILIK is a wellness and planning tool. It is not a licensed medical care provider, medical device, diagnostic tool, or contraception method. The Services do not constitute medical advice, diagnosis, or treatment.</p>
    <p style={{ marginBottom: 8 }}>7.1 The Services provide general information about cycle-aware planning for personal wellbeing purposes only. All cycle predictions are estimates based on the data you provide and may not reflect your individual physiology.</p>
    <p style={{ marginBottom: 8 }}>7.2 You must always consult a qualified healthcare professional for medical advice, contraception, irregular cycles, or any health concerns.</p>
    <p style={{ marginBottom: 8 }}>7.3 The Services must not be used for contraceptive purposes or relied upon to prevent pregnancy.</p>
    <p style={{ marginBottom: 20 }}>7.4 Use of the Services does not establish a doctor-patient relationship. If you think you have a medical emergency, call the emergency services immediately.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>8. Subscription Terms</p>
    <p style={{ marginBottom: 8 }}><strong>8.1 Free and premium tiers</strong><br/>The Services offer a free tier providing access to core features, and a premium tier called ILIK+ which provides access to additional features including the multi-month forward calendar, partner sharing, and early access to new features.</p>
    <p style={{ marginBottom: 8 }}><strong>8.2 Pricing</strong><br/>ILIK+ is available at EUR 2.00 per month at the current early bird rate. This rate is guaranteed for the lifetime of your subscription and will not increase as long as your subscription remains active and uninterrupted.</p>
    <p style={{ marginBottom: 8 }}><strong>8.3 Payment processing</strong><br/>Subscriptions purchased through ilikapp.com are billed monthly through Stripe. Subscriptions purchased through the Apple App Store are processed by Apple and governed by the terms of your Apple ID. Subscriptions purchased through Google Play (where available) are processed by Google and governed by the terms of your Google account. ILIK does not collect, store, or process your payment card details directly.</p>
    <p style={{ marginBottom: 8 }}><strong>8.4 Automatic renewal</strong><br/>Subscriptions renew automatically at the end of each monthly billing cycle unless cancelled before the renewal date.</p>
    <p style={{ marginBottom: 8 }}><strong>8.5 Managing and cancelling your subscription</strong><br/>Where you purchased your subscription affects how you manage and cancel it:</p>
    <ul style={{ paddingLeft: 20, marginBottom: 8 }}>
      <li style={{ marginBottom: 4 }}>Purchased on ilikapp.com (Stripe): you may cancel at any time through your account settings in the app.</li>
      <li style={{ marginBottom: 4 }}>Purchased through the Apple App Store: you must manage and cancel your subscription through your Apple ID settings. We are unable to cancel Apple-managed subscriptions on your behalf.</li>
      <li style={{ marginBottom: 4 }}>Purchased through Google Play: you must manage and cancel your subscription through your Google Play account. We are unable to cancel Google-managed subscriptions on your behalf.</li>
    </ul>
    <p style={{ marginBottom: 8 }}>Cancellation takes effect at the end of the current billing period. You will retain access to ILIK+ features until the end of the period you have paid for.</p>
    <p style={{ marginBottom: 8 }}><strong>8.6 Refunds</strong><br/>Refunds and withdrawal rights are governed by applicable consumer protection law. If you are a consumer resident in the European Union, you benefit from the rights set out in the Consumer Rights Directive and the Dutch Burgerlijk Wetboek. For subscriptions purchased through Apple or Google, refund requests are processed by Apple or Google under their respective policies. For subscriptions purchased through ilikapp.com, contact support@ilikapp.com within 30 days of the charge if you believe you have been charged in error.</p>
    <p style={{ marginBottom: 20 }}><strong>8.7 Changes to pricing</strong><br/>We reserve the right to change subscription pricing for new subscribers at any time. Existing subscribers who signed up at the early bird rate will not be affected by pricing changes as long as their subscription remains active and uninterrupted.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>9. Partner Sharing Feature</p>
    <p style={{ marginBottom: 8 }}>9.1 The Share with Partner feature is available to ILIK+ subscribers. It generates a unique anonymous link that allows a trusted person to view a read-only version of your seasonal calendar and education cards.</p>
    <p style={{ marginBottom: 8 }}>9.2 By sharing a partner link, you take full responsibility for the decision to share your cycle information with the recipient.</p>
    <p style={{ marginBottom: 20 }}>9.3 The partner view does not display your name, email address, events, or any personal account information.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>10. Disclaimers</p>
    <p style={{ marginBottom: 8 }}>10.1 The Services are provided on an "as-is" and "as-available" basis. To the extent permitted by law, we make no representations or warranties that:</p>
    <ul style={{ paddingLeft: 20, marginBottom: 20 }}>
      <li style={{ marginBottom: 4 }}>The Services will be free from errors or interruptions;</li>
      <li style={{ marginBottom: 4 }}>The Services will be accessible or available at all times;</li>
      <li style={{ marginBottom: 4 }}>Information provided through the Services is accurate or complete for any particular individual;</li>
      <li style={{ marginBottom: 4 }}>Phase predictions and cycle calculations will match your actual cycle.</li>
    </ul>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>11. Limitation of Liability</p>
    <p style={{ marginBottom: 8 }}>11.1 Nothing in these Terms shall limit or exclude liability for death or personal injury caused by negligence, for fraud or fraudulent misrepresentation, or for any other liability which cannot lawfully be limited or excluded under Dutch or EU law.</p>
    <p style={{ marginBottom: 8 }}>11.2 Subject to clause 11.1, ILIK B.V. shall not be liable for any indirect, incidental, special, or consequential loss or damage arising out of or in connection with your use of the Services.</p>
    <p style={{ marginBottom: 20 }}>11.3 ILIK B.V. expressly disclaims any liability for unintended pregnancy or any other health outcomes resulting from reliance on the Services. The Services are a planning tool and must not be used for contraception.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>12. Service Availability</p>
    <p style={{ marginBottom: 8 }}>12.1 The Services rely on third-party cloud infrastructure. While we will do everything reasonably possible to maintain availability, we are not responsible for disruptions caused by factors outside our reasonable control, including internet connectivity issues, third-party service outages, or device failures.</p>
    <p style={{ marginBottom: 20 }}>12.2 We reserve the right to perform maintenance, updates, or modifications to the Services at any time. We will endeavour to notify users of planned downtime where reasonably practicable.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>13. Force Majeure</p>
    <p style={{ marginBottom: 20 }}>13.1 We shall not be liable for any failure or delay in the performance of our obligations under these Terms resulting from events beyond our reasonable control, including but not limited to acts of God, natural disasters, war, terrorism, civil unrest, government actions, epidemics, labour disputes, power failures, failures of internet or telecommunications services, or failures of third-party service providers.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>14. Termination</p>
    <p style={{ marginBottom: 8 }}>14.1 You may delete your account at any time via the in-app Settings, by using the "Delete All Data" option, or by contacting support@ilikapp.com. Upon deletion, your right to use the Services will immediately cease and all personal data will be permanently deleted within 30 days.</p>
    <p style={{ marginBottom: 8 }}>14.2 We reserve the right to suspend or terminate your account at any time if you breach these Terms, with or without prior notice depending on the severity of the breach.</p>
    <p style={{ marginBottom: 20 }}>14.3 Termination of your account does not affect any rights or liabilities that have accrued prior to termination.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>15. Changes to These Terms</p>
    <p style={{ marginBottom: 8 }}>15.1 We may revise these Terms from time to time. We will notify you of material changes by email or in-app notification before they take effect.</p>
    <p style={{ marginBottom: 8 }}>15.2 Continued use of the Services following notification of changes constitutes your acceptance of the revised Terms.</p>
    <p style={{ marginBottom: 20 }}>15.3 The current version of these Terms is always available at ilikapp.com/terms.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>16. Governing Law and Jurisdiction</p>
    <p style={{ marginBottom: 8 }}>16.1 These Terms shall be governed by and construed in accordance with the laws of the Netherlands and, where applicable, the European Union.</p>
    <p style={{ marginBottom: 8 }}>16.2 Any disputes arising in connection with these Terms shall first be submitted to informal resolution by contacting support@ilikapp.com. If the dispute cannot be resolved informally, it shall be subject to the exclusive jurisdiction of the competent courts of the Netherlands.</p>
    <p style={{ marginBottom: 20 }}>16.3 If you are a consumer resident in the EU, you also have the right to submit a complaint to the European Online Dispute Resolution platform at ec.europa.eu/consumers/odr.</p>

    <p style={{ fontWeight: 700, marginBottom: 8 }}>17. Contact</p>
    <p>For any questions or concerns about these Terms, please contact us at support@ilikapp.com.</p>
  </LegalPage>
);

const SettingsTab = ({ settings, onSave, events, onSignOut, onSharePartner, onPreviewIntro }) => {
  const [formData, setFormData] = useState({
    cycle_length: settings?.cycle_length || 28,
    period_length: settings?.period_length || 5,
    last_period_start: settings?.last_period_start || new Date().toISOString().split('T')[0]
  });

  // State for all settings sections
  const [showCycleDetailsSheet, setShowCycleDetailsSheet] = useState(false);
  const [contraceptiveMode, setContraceptiveMode] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [showCycleHistory, setShowCycleHistory] = useState(false);
  const [cycleHistory, setCycleHistory] = useState({ cycles: [], average_cycle_length: null });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [showPaywallConfirm, setShowPaywallConfirm] = useState(false);
  const [showSharePartner, setShowSharePartner] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);

  // Check premium status on mount
  useEffect(() => {
    checkIsPremium().then(result => setIsPremium(result));
  }, []);

  const handleRestore = async () => {
    setIsRestoring(true);
    try {
      const result = await restorePurchases();
      if (result && result.isPremium) {
        await updateIsPremium(true);
        setIsPremium(true);
        toast.success('Purchases restored!');
      } else {
        toast.error('No active purchases found');
      }
    } catch {
      toast.error('Restore failed. Please try again.');
    }
    setIsRestoring(false);
  };

  const handlePaywallJoin = () => {
    // Log the tap for tracking interest
    console.log('ILIK Premium - User tapped "Join ILIK" button (from Settings)');
    const joinCount = parseInt(localStorage.getItem('ilik_premium_interest') || '0') + 1;
    localStorage.setItem('ilik_premium_interest', joinCount.toString());
    
    setShowPaywall(false);
    setIsPremium(true);
    setShowPaywallConfirm(true);
    
    setTimeout(() => {
      setShowPaywallConfirm(false);
    }, 3000);
  };

  useEffect(() => {
    if (settings) {
      setFormData({
        cycle_length: settings.cycle_length,
        period_length: settings.period_length,
        last_period_start: settings.last_period_start
      });
    }
  }, [settings]);

  // Fetch preferences on mount
  useEffect(() => {
    const fetchPreferences = async () => {
      const enabled = await getNotificationsEnabledSupabase();
      setNotificationsEnabled(enabled);
      const contraceptive = await getContraceptiveModeSupabase();
      setContraceptiveMode(contraceptive);
    };
    fetchPreferences();
  }, []);

  // Fetch cycle history when opening that screen
  useEffect(() => {
    if (showCycleHistory) {
      const fetchHistory = async () => {
        try {
          const response = await axios.get(`${API}/cycle/history`);
          setCycleHistory(response.data);
        } catch (error) {
          console.error("Error fetching cycle history:", error);
        }
      };
      fetchHistory();
    }
  }, [showCycleHistory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onSave(formData);
    setShowCycleDetailsSheet(false);
  };

  const handleContraceptiveToggle = async () => {
    const newValue = !contraceptiveMode;
    setContraceptiveMode(newValue);
    const ok = await setContraceptiveModeSupabase(newValue);
    if (!ok) console.error("Error saving contraceptive mode preference to Supabase");
  };

  const handleNotificationsToggle = async () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    const ok = await setNotificationsEnabledSupabase(newValue);
    if (!ok) console.error("Error saving notifications preference to Supabase");
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API}/data/export`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ilik-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("Data exported successfully!");
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("Failed to export data");
    }
  };

  const handleDeleteAllData = async () => {
    try {
      await axios.delete(`${API}/data/all`);
      toast.success("All data deleted");
      setShowDeleteConfirm(false);
      window.location.reload();
    } catch (error) {
      console.error("Error deleting data:", error);
      toast.error("Failed to delete data");
    }
  };

  const handleComingSoon = () => {
    toast.info("Coming soon!");
  };

  const handleResetAppData = async () => {
    try {
      // Clear all localStorage data
      localStorage.clear();
      
      // Clear backend data
      await axios.delete(`${API}/data/all`);
      
      toast.success("App data reset successfully");
      setShowResetConfirm(false);
      
      // Force reload to show onboarding
      window.location.reload();
    } catch (error) {
      console.error("Error resetting app data:", error);
      // Even if backend fails, clear localStorage and reload
      localStorage.clear();
      window.location.reload();
    }
  };

  const formatDateRange = (start, end) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const options = { month: 'short', day: 'numeric' };
    const yearOptions = { year: 'numeric' };
    return `${startDate.toLocaleDateString('en-US', options)} – ${endDate.toLocaleDateString('en-US', options)} ${endDate.toLocaleDateString('en-US', yearOptions)}`;
  };

  // Cycle History Screen
  if (showCycleHistory) {
    return (
      <div className="settings-screen" data-testid="cycle-history-screen">
        <div className="settings-header-bar">
          <button className="settings-back-btn" onClick={() => setShowCycleHistory(false)} data-testid="back-from-history">
            <ChevronLeft size={24} />
          </button>
          <h2 className="settings-screen-title">My Cycle History</h2>
          <div style={{ width: 24 }}></div>
        </div>

        {cycleHistory.average_cycle_length && (
          <div className="history-avg-pill">
            Average cycle: {cycleHistory.average_cycle_length} days
          </div>
        )}

        {cycleHistory.cycles.length === 0 ? (
          <div className="history-empty-state">
            <img src={SPRING_FLOWER_URL} alt="Spring" className="history-empty-icon" />
            <p className="history-empty-text">Your cycle history will appear here after your first complete cycle.</p>
          </div>
        ) : (
          <div className="history-list">
            {cycleHistory.cycles.map((cycle) => (
              <div key={cycle.cycle_number} className="history-card">
                <div className="history-card-header">
                  <span className="history-cycle-number">Cycle {cycle.cycle_number}</span>
                  <span className="history-cycle-days">{cycle.total_days} days</span>
                </div>
                <p className="history-date-range">{formatDateRange(cycle.start_date, cycle.end_date)}</p>
                <div className="history-phase-strip">
                  <div className="phase-strip-winter" style={{ flex: cycle.winter_days }}></div>
                  <div className="phase-strip-spring" style={{ flex: cycle.spring_days }}></div>
                  <div className="phase-strip-summer" style={{ flex: cycle.summer_days }}></div>
                  <div className="phase-strip-autumn" style={{ flex: cycle.autumn_days }}></div>
                </div>
                <p className="history-events-count">Showed up {cycle.events_count} times</p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <>
    <div className="settings-screen" data-testid="settings-tab">
      <h1 className="settings-page-title">Settings</h1>

      {/* SECTION 1 — MY CYCLE */}
      <div className="settings-section">
        <span className="settings-section-label">MY CYCLE</span>
        <button className="settings-row-card settings-row-clickable" onClick={() => setShowCycleDetailsSheet(true)} data-testid="cycle-details-row">
          <div className="settings-icon-box settings-icon-orange">
            <span style={{ fontSize: '18px' }}>🔄</span>
          </div>
          <div className="settings-row-content">
            <span className="settings-row-title">Cycle Details</span>
            <span className="settings-row-subtitle">Cycle length, period length, last period start</span>
          </div>
          <ChevronRight size={20} className="settings-row-chevron" />
        </button>
      </div>

      {/* SECTION 2 — CONTRACEPTION */}
      <div className="settings-section">
        <span className="settings-section-label">CONTRACEPTION</span>
        <div className="settings-row-card">
          <div className="settings-row-clickable" style={{ borderRadius: contraceptiveMode ? '12px 12px 0 0' : '12px' }}>
            <div className="settings-icon-box settings-icon-yellow">
              <span style={{ fontSize: '18px' }}>💊</span>
            </div>
            <div className="settings-row-content">
              <span className="settings-row-title">Contraceptive Mode</span>
              <span className="settings-row-subtitle">Using hormonal contraception may affect phase timing. ILIK still works as a rhythm awareness tool.</span>
            </div>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={contraceptiveMode}
                onChange={handleContraceptiveToggle}
                data-testid="contraceptive-toggle"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
          {contraceptiveMode && (
            <div className="contraceptive-info-card" data-testid="contraceptive-info">
              <p>If you are on the combined pill, ovulation is suppressed and your phases may not follow the standard pattern. ILIK can still help you understand your body's rhythm, but phase timing will be approximate.</p>
              <p>If you are on the mini-pill (progestin-only), some women continue to ovulate. Your Summer phase window may be shorter or less predictable. Tracking how you feel each day matters more than the calendar in this case.</p>
              <p>When in doubt, speak with your doctor about which type of contraception you are taking and how it may affect your cycle.</p>
            </div>
          )}
        </div>
      </div>

      {/* SECTION — PREMIUM */}
      <div className="settings-section">
        <span className="settings-section-label">ILIK+</span>
        {isPremium ? (
          <div className="settings-row-card" data-testid="premium-status-active">
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.25rem 0' }}>
              <div className="settings-icon-box" style={{ background: 'rgba(67,160,71,0.12)' }}>
                <Check size={20} color="#43A047" />
              </div>
              <div className="settings-row-content">
                <span className="settings-row-title" style={{ color: '#43A047' }}>ILIK+ Active</span>
              </div>
            </div>
          </div>
        ) : (
          <button
            className="settings-row-card settings-row-clickable"
            onClick={() => setShowPaywall(true)}
            data-testid="premium-status-upgrade"
          >
            <div className="settings-icon-box" style={{ background: 'rgba(255,143,11,0.12)' }}>
              <Star size={20} color="#FF8F0B" />
            </div>
            <div className="settings-row-content">
              <span className="settings-row-title">Free Plan</span>
              <span style={{ fontSize: '0.75rem', color: '#FF8F0B', fontWeight: 600 }}>Upgrade to ILIK+</span>
            </div>
            <ChevronRight size={20} className="settings-row-arrow" />
          </button>
        )}
        <button 
          className="settings-row-card settings-row-clickable" 
          onClick={async () => { const premium = await checkIsPremium(); if (premium) { setShowSharePartner(true); } else { setShowPaywall(true); } }}
          data-testid="share-partner-row"
        >
          <div className="settings-icon-box settings-icon-red">
            <Heart size={20} fill="#E53935" />
          </div>
          <div className="settings-row-content">
            <span className="settings-row-title">Share with Partner</span>
          </div>
          <ChevronRight size={20} className="settings-row-arrow" />
        </button>
        {isNative() && (
          <button
            className="settings-row-card settings-row-clickable"
            onClick={handleRestore}
            disabled={isRestoring}
            data-testid="restore-purchases-row"
          >
            <div className="settings-icon-box" style={{ background: 'rgba(255,143,11,0.12)' }}>
              <Download size={20} color="#FF8F0B" />
            </div>
            <div className="settings-row-content">
              <span className="settings-row-title">{isRestoring ? 'Restoring...' : 'Restore Purchases'}</span>
            </div>
            <ChevronRight size={20} className="settings-row-arrow" />
          </button>
        )}
      </div>

      {/* SECTION — NOTIFICATIONS */}
      <div className="settings-section">
        <span className="settings-section-label">NOTIFICATIONS</span>
        <div className="settings-row-card">
          <div className="settings-row-clickable">
            <div className="settings-icon-box settings-icon-blue">
              <Bell size={20} />
            </div>
            <div className="settings-row-content">
              <span className="settings-row-title">Push Notifications</span>
              <span className="settings-row-subtitle">Get reminders for phase changes and events</span>
            </div>
            <label className="settings-toggle">
              <input
                type="checkbox"
                checked={notificationsEnabled}
                onChange={handleNotificationsToggle}
                data-testid="notifications-toggle"
              />
              <span className="toggle-slider"></span>
            </label>
          </div>
        </div>
      </div>

      {/* SECTION 5 — PRIVACY */}
      <div className="settings-section">
        <span className="settings-section-label">PRIVACY</span>
        <div className="privacy-card">
          <span className="privacy-shield">🛡️</span>
          <div className="privacy-text">
            <p>Your data stays on your device and your personal Supabase account.</p>
            <p>No data is shared with third parties.</p>
            <p>No advertising. No tracking.</p>
            <p>Your cycle is yours.</p>
          </div>
          <div className="privacy-divider"></div>
          <div className="privacy-links">
            <button className="privacy-link" onClick={() => setShowPrivacy(true)}>Privacy Policy</button>
            <button className="privacy-link" onClick={() => setShowTerms(true)}>Terms of Use</button>
          </div>
        </div>
      </div>

      {/* SECTION 6 — ACCOUNT */}
      <div className="settings-section">
        <span className="settings-section-label">ACCOUNT</span>
        <button className="settings-row-card settings-row-clickable settings-row-danger" onClick={() => setShowDeleteConfirm(true)} data-testid="delete-data-row">
          <div className="settings-icon-box settings-icon-red">
            <Trash2 size={20} />
          </div>
          <div className="settings-row-content">
            <span className="settings-row-title settings-row-title-danger">Delete All Data</span>
            <span className="settings-row-subtitle">This action cannot be undone</span>
          </div>
        </button>
        <button className="settings-row-card settings-row-clickable" onClick={onSignOut} data-testid="sign-out-row">
          <div className="settings-row-content">
            <span className="settings-row-title" style={{ color: '#757575' }}>Sign Out</span>
          </div>
        </button>
      </div>

      {/* Hidden preview link for testing */}
      <button
        onClick={onPreviewIntro}
        data-testid="preview-intro-link"
        style={{ display: 'block', margin: '2rem auto 0', background: 'none', border: 'none', color: '#9E9E9E', fontSize: '11px', cursor: 'pointer', padding: '8px' }}
      >
        Preview intro screen
      </button>

      {/* Cycle Details Bottom Sheet */}
      {showCycleDetailsSheet && (
        <div className="settings-sheet-overlay" onClick={() => setShowCycleDetailsSheet(false)}>
          <div className="settings-bottom-sheet cycle-details-sheet" onClick={(e) => e.stopPropagation()} data-testid="cycle-details-sheet">
            <div className="sheet-drag-handle"></div>
            <h3 className="sheet-title-large">My Cycle Details</h3>
            <form onSubmit={handleSubmit} className="sheet-form">
              <div className="form-group">
                <Label htmlFor="settings-cycle-length">Cycle Length (days)</Label>
                <Input
                  id="settings-cycle-length"
                  type="number"
                  min="21"
                  max="45"
                  value={formData.cycle_length}
                  onChange={(e) => setFormData({...formData, cycle_length: parseInt(e.target.value) || 28})}
                  data-testid="settings-cycle-length"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="settings-period-length">Period Length (days)</Label>
                <Input
                  id="settings-period-length"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.period_length}
                  onChange={(e) => setFormData({...formData, period_length: parseInt(e.target.value) || 5})}
                  data-testid="settings-period-length"
                />
              </div>

              <div className="form-group">
                <Label htmlFor="settings-last-period">Last Period Start</Label>
                <Input
                  id="settings-last-period"
                  type="date"
                  value={formData.last_period_start}
                  onChange={(e) => setFormData({...formData, last_period_start: e.target.value})}
                  data-testid="settings-last-period"
                />
              </div>

              <Button type="submit" className="save-btn" data-testid="save-settings-btn">
                Save Changes
              </Button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="settings-sheet-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="delete-confirm-modal" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <p className="delete-confirm-text">Are you sure? This will permanently delete all your cycle data and cannot be undone.</p>
            <div className="delete-confirm-buttons">
              <button className="delete-cancel-btn" onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
              <button className="delete-confirm-btn" onClick={handleDeleteAllData} data-testid="confirm-delete-btn">Delete Everything</button>
            </div>
          </div>
        </div>
      )}


      {/* Premium Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        onJoin={handlePaywallJoin}
      />


      {/* Share with Partner Modal */}
      <ShareWithPartnerModal
        isOpen={showSharePartner}
        onClose={() => setShowSharePartner(false)}
        userName={settings?.first_name || ''}
      />

      {/* Paywall Confirmation Toast */}
      {showPaywallConfirm && (
        <div className="paywall-confirm-toast" data-testid="paywall-confirm-settings">
          <Check size={20} />
          <span>You're on the list. We'll notify you when ILIK Premium launches.</span>
        </div>
      )}
    </div>

    {showPrivacy && <PrivacyPolicyPage onBack={() => setShowPrivacy(false)} />}
    {showTerms && <TermsOfUsePage onBack={() => setShowTerms(false)} />}
    </>
  );
};
function MainApp() {
  const [isLoading, setIsLoading] = useState(true);
  const [hasSettings, setHasSettings] = useState(false);
  const [settings, setSettings] = useState(null);
  const settingsRef = useRef(null);
  const signInHandledRef = useRef(false);
  const [currentCycle, setCurrentCycle] = useState(null);
  const [calendarData, setCalendarData] = useState([]);
  const [patternData, setPatternData] = useState(null);
  const [activeTab, setActiveTab] = useState("cycle");
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showPeriodConfirm, setShowPeriodConfirm] = useState(false);
  const [events, setEvents] = useState([]);
  const [eventCounts, setEventCounts] = useState([]);
  const [showReflection, setShowReflection] = useState(false);
  const [lastCycleData, setLastCycleData] = useState(null);
  const [showTodayScreen, setShowTodayScreen] = useState(true);
  const [selectedSeason, setSelectedSeason] = useState(null);
  const [seasonSourceTab, setSeasonSourceTab] = useState("pattern");
  const [authScreen, setAuthScreen] = useState(null); // null | "welcome" | "signin"
  const [showSharePartner, setShowSharePartner] = useState(false);
  const [showPreviewIntro, setShowPreviewIntro] = useState(false);
  const [showBloodDropTooltip, setShowBloodDropTooltip] = useState(false);
  const [tooltipFading, setTooltipFading] = useState(false);
  const [showPeriodBanner, setShowPeriodBanner] = useState(false);
  const [showNamePrompt, setShowNamePrompt] = useState(false);
  const [namePromptValue, setNamePromptValue] = useState('');
  const [namePromptError, setNamePromptError] = useState('');

  // Blood drop tooltip: show once after Today Screen is dismissed
  useEffect(() => {
    if (showTodayScreen || !hasSettings || isLoading) return;
    let mounted = true;
    const check = async () => {
      try {
        const seen = await checkBloodDropTooltipSeen();
        if (!mounted || seen) return;
        setTimeout(() => {
          if (!mounted) return;
          setShowBloodDropTooltip(true);
          // Auto-dismiss after 5 seconds
          setTimeout(() => {
            if (!mounted) return;
            setTooltipFading(true);
            setTimeout(() => {
              if (!mounted) return;
              setShowBloodDropTooltip(false);
              setTooltipFading(false);
              markBloodDropTooltipSeen();
            }, 300);
          }, 5000);
        }, 1000);
      } catch {}
    };
    check();
    return () => { mounted = false; };
  }, [showTodayScreen, hasSettings, isLoading]);

  // Period expected banner: show once per day when period_expected is true
  useEffect(() => {
    if (showTodayScreen || !hasSettings || isLoading || !currentCycle?.period_expected) return;
    const todayStr = new Date().toISOString().split('T')[0];
    const lastDismissed = localStorage.getItem('period_banner_dismissed_date');
    if (lastDismissed === todayStr) return;
    const timer = setTimeout(() => setShowPeriodBanner(true), 800);
    return () => clearTimeout(timer);
  }, [showTodayScreen, hasSettings, isLoading, currentCycle]);

  const handlePeriodBannerYes = async () => {
    setShowPeriodBanner(false);
    localStorage.setItem('period_banner_dismissed_date', new Date().toISOString().split('T')[0]);
    await saveNewPeriodStart();
  };

  const handlePeriodBannerNotYet = () => {
    setShowPeriodBanner(false);
    localStorage.setItem('period_banner_dismissed_date', new Date().toISOString().split('T')[0]);
  };

  // One-time name prompt for existing users who signed up before the name field was added
  useEffect(() => {
    if (showTodayScreen || !hasSettings || isLoading || authScreen) return;
    const name = settings?.first_name;
    if (name) return; // already has a name
    const dismissed = localStorage.getItem('name_prompt_dismissed');
    if (dismissed) return; // already dismissed once
    const timer = setTimeout(() => setShowNamePrompt(true), 1200);
    return () => clearTimeout(timer);
  }, [showTodayScreen, hasSettings, isLoading, authScreen, settings]);

  const handleNamePromptSave = async () => {
    const name = namePromptValue.trim();
    if (!name) { setNamePromptError('Please enter a name'); return; }
    try {
      await supabase.auth.updateUser({ data: { first_name: name } });
      localStorage.setItem('name_prompt_dismissed', 'true');
      setSettings(prev => ({ ...prev, first_name: name }));
      settingsRef.current = { ...settingsRef.current, first_name: name };
    } catch {}
    setShowNamePrompt(false);
  };

  const handleNamePromptSkip = () => {
    localStorage.setItem('name_prompt_dismissed', 'true');
    setShowNamePrompt(false);
  };

  // ── Supabase-first settings loader ──────────────────────────────
  // Priority: 1. Supabase  2. Backend  3. localStorage
  // Returns the settings object (or null) so callers can use it immediately.
  const fetchSettings = useCallback(async () => {
    let settingsData = null;

    // 1. PRIMARY — Supabase
    try {
      const sbData = await loadUserCycleFromSupabase();
      if (sbData && sbData.lastPeriodStartDate) {
        settingsData = {
          email: sbData.email || '',
          cycle_length: sbData.cycleLength || 28,
          period_length: sbData.periodLength || 5,
          last_period_start: sbData.lastPeriodStartDate,
        };
        console.log('✅ Settings loaded from Supabase (primary)');
      }
    } catch (err) {
      console.warn('Supabase settings load failed:', err.message);
    }

    // 2. FALLBACK — Backend
    if (!settingsData) {
      try {
        const response = await axios.get(`${API}/settings`);
        if (response.data && response.data.last_period_start) {
          settingsData = response.data;
          console.log('✅ Settings loaded from backend (fallback)');
        }
      } catch (err) {
        console.warn('Backend settings load failed:', err.message);
      }
    }

    // 3. LAST RESORT — localStorage
    if (!settingsData) {
      try {
        const raw = localStorage.getItem('user_data');
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed.last_period_start_date) {
            settingsData = {
              email: parsed.email || '',
              cycle_length: parsed.cycle_length || 28,
              period_length: parsed.period_length || 5,
              last_period_start: parsed.last_period_start_date,
            };
            console.log('✅ Settings loaded from localStorage (last resort)');
          }
        }
      } catch (err) {
        console.warn('localStorage settings load failed:', err.message);
      }
    }

    if (settingsData) {
      setSettings(settingsData);
      settingsRef.current = settingsData;
      setHasSettings(true);
      return settingsData;
    }
    return null;
  }, []);

  // ── Client-side cycle computation from settings ref ────────────
  const fetchCurrentCycle = useCallback(async () => {
    const s = settingsRef.current;
    if (s && s.last_period_start) {
      setCurrentCycle(calculateCyclePhaseClient(s.cycle_length, s.period_length, s.last_period_start));
      return;
    }
    // Backend fallback
    try {
      const response = await axios.get(`${API}/cycle/current`);
      setCurrentCycle(response.data);
    } catch (error) {
      console.error("Error fetching current cycle:", error);
    }
  }, []);

  const fetchCalendarData = useCallback(async () => {
    const s = settingsRef.current;
    const year = selectedMonth.getFullYear();
    const month = selectedMonth.getMonth() + 1;
    if (s && s.last_period_start) {
      setCalendarData(generateCalendarDataClient(s.cycle_length, s.period_length, s.last_period_start, year, month));
      return;
    }
    // Backend fallback
    try {
      const response = await axios.get(`${API}/cycle/calendar?year=${year}&month=${month}`);
      setCalendarData(response.data.days || []);
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    }
  }, [selectedMonth]);

  const fetchPatternData = useCallback(async () => {
    const s = settingsRef.current;
    if (s) {
      setPatternData({ cycle_length: s.cycle_length || 28, period_length: s.period_length || 5, total_logs: 0, recent_logs: [] });
      return;
    }
    // Backend fallback
    try {
      const response = await axios.get(`${API}/cycle/pattern`);
      setPatternData(response.data);
    } catch (error) {
      console.error("Error fetching pattern data:", error);
    }
  }, []);

  const fetchEvents = useCallback(async () => {
    const sbEvents = await loadEventsFromSupabase();
    if (sbEvents !== null) { setEvents(sbEvents); return; }
    console.error("Error fetching events from Supabase");
    toast.error("Couldn't load your events. Please check your connection.");
  }, []);

  const fetchEventCounts = useCallback(async () => {
    const sbCounts = await getEventCountsByDateFromSupabase();
    if (sbCounts !== null) { setEventCounts(sbCounts); return; }
    console.error("Error fetching event counts from Supabase");
  }, []);

  const fetchLastCycleSummary = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/cycle/last-cycle-summary`);
      setLastCycleData(response.data);
    } catch (error) {
      console.error("Error fetching last cycle summary:", error);
    }
  }, []);

  const handleAddEvent = async (eventData) => {
    const sbResult = await saveEventToSupabase(eventData);
    if (sbResult) {
      toast.success("Event saved!");
      await fetchEvents();
      await fetchEventCounts();
      return;
    }
    toast.error("Couldn't save your event. Please check your connection.");
  };

  const handleDeleteEvent = async (eventId) => {
    const sbOk = await deleteEventFromSupabase(eventId);
    if (sbOk) {
      toast.success("Event deleted");
      await fetchEvents();
      await fetchEventCounts();
      return;
    }
    toast.error("Couldn't delete your event. Please check your connection.");
  };

  const handleSaveSettings = async (formData) => {
    try {
      // 1. PRIMARY — Save to Supabase first
      await updateUserCycleInSupabase(
        formData.last_period_start,
        formData.cycle_length,
        formData.period_length
      );

      // 2. SECONDARY — Sync to backend (non-blocking)
      axios.post(`${API}/settings`, formData).catch(err => console.warn('Backend save failed:', err.message));

      // Update localStorage
      const existingData = JSON.parse(localStorage.getItem('user_data') || '{}');
      localStorage.setItem('user_data', JSON.stringify({
        ...existingData,
        last_period_start_date: formData.last_period_start,
        cycle_length: formData.cycle_length,
        period_length: formData.period_length,
      }));

      // Apply locally — no need to re-fetch
      const newSettings = { ...settings, ...formData };
      setSettings(newSettings);
      settingsRef.current = newSettings;
      setHasSettings(true);
      setCurrentCycle(calculateCyclePhaseClient(newSettings.cycle_length, newSettings.period_length, newSettings.last_period_start));
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      setCalendarData(generateCalendarDataClient(newSettings.cycle_length, newSettings.period_length, newSettings.last_period_start, year, month));
      setPatternData({ cycle_length: newSettings.cycle_length, period_length: newSettings.period_length, total_logs: 0, recent_logs: [] });

      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
      console.error(error);
    }
  };

  const handlePeriodLogClick = () => {
    // Dismiss tooltip if visible
    if (showBloodDropTooltip) {
      setShowBloodDropTooltip(false);
      markBloodDropTooltipSeen();
    }
    setShowPeriodConfirm(true);
  };

  const handleConfirmPeriodLog = async () => {
    setShowPeriodConfirm(false);
    
    // Check if there's previous cycle data with events
    try {
      const summaryResponse = await axios.get(`${API}/cycle/last-cycle-summary`);
      const hasEventsInLastCycle = summaryResponse.data?.events_count > 0;
      
      if (hasEventsInLastCycle) {
        // Show reflection screen if she has previous cycle data
        setLastCycleData(summaryResponse.data);
        setShowReflection(true);
      } else {
        // Skip straight to Day 1 if no previous data
        await saveNewPeriodStart();
      }
    } catch (error) {
      // If error fetching, just save directly
      await saveNewPeriodStart();
    }
  };

  const saveNewPeriodStart = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const newSettings = { ...settings, last_period_start: today };

      // 1. PRIMARY — Save to Supabase
      await updateUserCycleInSupabase(today, newSettings.cycle_length, newSettings.period_length);

      // 2. SECONDARY — Sync to backend (non-blocking)
      axios.post(`${API}/settings`, newSettings).catch(err => console.warn('Backend save failed:', err.message));

      // Update localStorage
      const existingData = JSON.parse(localStorage.getItem('user_data') || '{}');
      localStorage.setItem('user_data', JSON.stringify({ ...existingData, last_period_start_date: today }));

      // Apply locally
      setSettings(newSettings);
      settingsRef.current = newSettings;
      setShowPeriodBanner(false);
      setCurrentCycle(calculateCyclePhaseClient(newSettings.cycle_length, newSettings.period_length, today));
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      setCalendarData(generateCalendarDataClient(newSettings.cycle_length, newSettings.period_length, today, year, month));
      setPatternData({ cycle_length: newSettings.cycle_length, period_length: newSettings.period_length, total_logs: 0, recent_logs: [] });

      toast.success("Period logged! You're now on Day 1.");
    } catch (error) {
      toast.error("Failed to log period");
      console.error(error);
    }
  };

  const handleBeginNewCycle = async () => {
    setShowReflection(false);
    await saveNewPeriodStart();
  };

  const handleOnboardingComplete = async () => {
    try { await markOnboardingCompleted(); } catch {}
    setAuthScreen(null);

    // Read from localStorage synchronously (just saved during onboarding) — no loading flash
    const raw = localStorage.getItem('user_data');
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        const settingsData = {
          email: parsed.email || '',
          first_name: parsed.first_name || '',
          cycle_length: parsed.cycle_length || 28,
          period_length: parsed.period_length || 5,
          last_period_start: parsed.last_period_start_date,
        };
        setSettings(settingsData);
        settingsRef.current = settingsData;
        setHasSettings(true);
        setCurrentCycle(calculateCyclePhaseClient(settingsData.cycle_length, settingsData.period_length, settingsData.last_period_start));
        const year = selectedMonth.getFullYear();
        const month = selectedMonth.getMonth() + 1;
        setCalendarData(generateCalendarDataClient(settingsData.cycle_length, settingsData.period_length, settingsData.last_period_start, year, month));
        setPatternData({ cycle_length: settingsData.cycle_length, period_length: settingsData.period_length, total_logs: 0, recent_logs: [] });
      } catch {}
    }
    // Background: load events only (settings already set from localStorage above)
    fetchEvents().catch(() => {});
    fetchEventCounts().catch(() => {});
  };

  const handleSignInComplete = async () => {
    // Mark that sign-in flow is handling routing — prevents initApp from interfering
    signInHandledRef.current = true;

    // 1. Direct Supabase query — single source of truth
    let cycleRow = null;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      console.log('[handleSignInComplete] userId:', userId);
      if (userId) {
        const { data, error } = await supabase
          .from('user_cycles')
          .select('last_period_start_date, cycle_length, period_length, onboarding_completed')
          .eq('user_id', userId)
          .maybeSingle();
        console.log('[handleSignInComplete] user_cycles query:', { data, error: error?.message });
        if (!error && data) cycleRow = data;
      }
    } catch (err) {
      console.warn('[handleSignInComplete] Supabase query failed:', err.message);
    }

    // Read first_name from auth user metadata (identity layer)
    let firstName = '';
    if (cycleRow) {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        firstName = user?.user_metadata?.first_name || '';
      } catch {}
    }

    // 2. If last_period_start_date exists → user is onboarded, go to home
    if (cycleRow && cycleRow.last_period_start_date) {
      console.log('[handleSignInComplete] Returning user — loading data and going home');
      const settingsData = {
        first_name: firstName,
        cycle_length: cycleRow.cycle_length || 28,
        period_length: cycleRow.period_length || 5,
        last_period_start: cycleRow.last_period_start_date,
      };
      setSettings(settingsData);
      settingsRef.current = settingsData;
      setHasSettings(true);
      setCurrentCycle(calculateCyclePhaseClient(settingsData.cycle_length, settingsData.period_length, settingsData.last_period_start));
      const year = selectedMonth.getFullYear();
      const month = selectedMonth.getMonth() + 1;
      setCalendarData(generateCalendarDataClient(settingsData.cycle_length, settingsData.period_length, settingsData.last_period_start, year, month));
      setPatternData({ cycle_length: settingsData.cycle_length, period_length: settingsData.period_length, total_logs: 0, recent_logs: [] });
      fetchEvents().catch(() => {});
      fetchEventCounts().catch(() => {});

      // RevenueCat: init + sync premium status on native
      initRevenueCat().then(() => {
        checkPremiumStatus().then(rcPremium => {
          if (rcPremium === true) updateIsPremium(true);
          else if (rcPremium === false) updateIsPremium(false);
        });
      }).catch(() => {});

      setAuthScreen(null);
      setIsLoading(false);
      return;
    }

    // 3. No cycle data → show onboarding
    console.log('[handleSignInComplete] No cycle data found — showing onboarding');
    setAuthScreen('cycle_setup');
  };

  const handleSignOut = async () => {
    if (supabase) await supabase.auth.signOut();
    localStorage.clear();
    settingsRef.current = null;
    signInHandledRef.current = false;
    setHasSettings(false);
    setSettings(null);
    setCurrentCycle(null);
    setShowTodayScreen(true);
    setAuthScreen('welcome');
  };

  useEffect(() => {
    const initApp = async () => {
      // If sign-in flow already handled routing, don't override it
      if (signInHandledRef.current) return;

      // Check URL params for direct login
      const urlParams = new URLSearchParams(window.location.search);
      const directLogin = urlParams.get('login') === 'true';

      // Check Supabase session first
      if (supabase) {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          setAuthScreen(directLogin ? 'signin' : 'welcome');
          setIsLoading(false);
          return;
        }

        // Session exists — direct Supabase query for cycle data
        let cycleRow = null;
        try {
          const userId = session.user?.id;
          console.log('[initApp] userId:', userId);
          if (userId) {
            const { data, error } = await supabase
              .from('user_cycles')
              .select('last_period_start_date, cycle_length, period_length, onboarding_completed')
              .eq('user_id', userId)
              .maybeSingle();
            console.log('[initApp] user_cycles query:', { data, error: error?.message });
            if (!error && data) cycleRow = data;
          }
        } catch (err) {
          console.warn('[initApp] Supabase query failed:', err.message);
        }

        // Read first_name from auth user metadata (identity layer)
        let firstName = '';
        if (cycleRow) {
          firstName = session.user?.user_metadata?.first_name || '';
        }

        if (cycleRow && cycleRow.last_period_start_date) {
          console.log('[initApp] Returning user — loading data and going home');
          const settingsData = {
            first_name: firstName,
            cycle_length: cycleRow.cycle_length || 28,
            period_length: cycleRow.period_length || 5,
            last_period_start: cycleRow.last_period_start_date,
          };
          setSettings(settingsData);
          settingsRef.current = settingsData;
          setHasSettings(true);
          setCurrentCycle(calculateCyclePhaseClient(settingsData.cycle_length, settingsData.period_length, settingsData.last_period_start));
          const year = selectedMonth.getFullYear();
          const month = selectedMonth.getMonth() + 1;
          setCalendarData(generateCalendarDataClient(settingsData.cycle_length, settingsData.period_length, settingsData.last_period_start, year, month));
          setPatternData({ cycle_length: settingsData.cycle_length, period_length: settingsData.period_length, total_logs: 0, recent_logs: [] });
          fetchEvents().catch(() => {});
          fetchEventCounts().catch(() => {});
          initRevenueCat().then(() => {
            checkPremiumStatus().then(rcPremium => {
              if (rcPremium === true) updateIsPremium(true);
              else if (rcPremium === false) updateIsPremium(false);
            });
          }).catch(() => {});
          setIsLoading(false);
          return;
        }

        // No cycle data found — show onboarding
        console.log('[initApp] No cycle data found — showing onboarding');
        setAuthScreen('cycle_setup');
        setIsLoading(false);
        return;
      }
      
      setIsLoading(false);
    };
    initApp();
  }, [fetchSettings, fetchEvents, fetchEventCounts]);

  // Listen for auth state changes (sign out)
  useEffect(() => {
    if (!supabase) return;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        setAuthScreen('welcome');
        setHasSettings(false);
        setSettings(null);
        setCurrentCycle(null);
        setShowTodayScreen(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (hasSettings) {
      fetchCalendarData();
    }
  }, [selectedMonth, hasSettings, fetchCalendarData]);

  if (isLoading) {
    return (
      <div className="loading-screen" data-testid="loading-screen">
        <div className="loading-content">
          <h1 className="app-logo">ILIK</h1>
          <Droplet size={32} fill="#E53935" color="#E53935" className="loading-breathe" style={{ display: 'block', margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  if (authScreen === 'welcome') {
    return (
      <>
        <WelcomeAuthScreen
          onGetStarted={() => setAuthScreen('onboarding')}
          onSignIn={() => setAuthScreen('signin')}
        />
        <Toaster />
      </>
    );
  }

  if (authScreen === 'signin') {
    return (
      <>
        <SignInScreen
          onSignInComplete={handleSignInComplete}
          onBack={() => window.location.href = '/'}
          onSignUp={() => setAuthScreen('onboarding')}
        />
        <Toaster />
      </>
    );
  }

  if (authScreen === 'onboarding') {
    return (
      <>
        <OnboardingScreen
          onComplete={handleOnboardingComplete}
          onSwitchToSignIn={() => setAuthScreen('signin')}
          startAtCycleSetup={false}
        />
        <Toaster />
      </>
    );
  }

  if (authScreen === 'cycle_setup') {
    return (
      <>
        <OnboardingScreen
          onComplete={handleOnboardingComplete}
          startAtCycleSetup={true}
        />
        <Toaster />
      </>
    );
  }

  // Show reflection screen when logging new period
  if (showReflection) {
    return (
      <>
        <CycleReflectionScreen 
          lastCycleData={lastCycleData} 
          onBeginNewCycle={handleBeginNewCycle} 
        />
        <Toaster />
      </>
    );
  }

  // Show Seasons Intro preview (from Settings link)
  if (showPreviewIntro) {
    return (
      <>
        <SeasonsIntroContent showBack onBack={() => setShowPreviewIntro(false)} onAction={() => setShowPreviewIntro(false)} actionLabel="Close preview" />
        <Toaster />
      </>
    );
  }

  // Show Today Screen on app open
  if (showTodayScreen && currentCycle) {
    return (
      <>
        <TodayScreen 
          currentCycle={currentCycle}
          settings={settings}
          onEnter={() => setShowTodayScreen(false)} 
        />
        <Toaster />
      </>
    );
  }

  return (
    <div className="app-container" data-testid="main-app">
      {/* Header */}
      <header className="app-header" data-testid="app-header">
        <div className="header-brand">
          <h1 className="app-title">ILIK</h1>
          <span className="app-tagline">4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></span>
        </div>
        <div style={{ position: 'relative' }}>
          <button 
            className={`period-log-btn${showBloodDropTooltip ? ' bd-pulse' : ''}`}
            onClick={handlePeriodLogClick}
            data-testid="log-period-btn"
            title="Log period start"
          >
            <Droplet size={24} />
          </button>
          {showBloodDropTooltip && (
            <div className={`bd-tooltip${tooltipFading ? ' bd-tooltip-fade' : ''}`} data-testid="blood-drop-tooltip">
              <div className="bd-tooltip-arrow" />
              <span>Tap to log your period.</span>
            </div>
          )}
        </div>
      </header>

      {/* Period Log Confirmation Modal */}
      {showPeriodConfirm && (
        <div className="modal-overlay" onClick={() => setShowPeriodConfirm(false)}>
          <div className="period-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="period-confirm-icon">
              <Droplet size={40} />
            </div>
            <h3>Log Period Start</h3>
            <p>Did your period start today?</p>
            <p className="period-confirm-note">This will update your cycle predictions.</p>
            {currentCycle && currentCycle.period_expected ? (
              <p style={{ color: '#E53935', textAlign: 'center', fontSize: '14px', fontWeight: 600, margin: '8px 0' }}>
                Your period is expected, logging will start a new cycle
              </p>
            ) : currentCycle && currentCycle.days_until_period > 0 && (
              <p style={{ color: '#E53935', textAlign: 'center', fontSize: '14px', fontWeight: 600, margin: '8px 0' }}>
                Period in {currentCycle.days_until_period} {currentCycle.days_until_period === 1 ? 'day' : 'days'}
              </p>
            )}
            <div className="period-confirm-buttons">
              <Button variant="outline" onClick={() => setShowPeriodConfirm(false)}>
                Cancel
              </Button>
              <Button className="confirm-period-btn" onClick={handleConfirmPeriodLog} data-testid="confirm-period-btn">
                Yes, Log Today
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Gentle period expected banner */}
      {showPeriodBanner && (
        <div className="period-banner" data-testid="period-expected-banner">
          <div className="period-banner-inner">
            <Droplet size={28} fill="#E53935" color="#E53935" className="period-banner-icon" />
            <p className="period-banner-question">Has your period arrived?</p>
            <div className="period-banner-actions">
              <button className="period-banner-btn period-banner-yes" onClick={handlePeriodBannerYes} data-testid="period-banner-yes">
                Yes, it started
              </button>
              <button className="period-banner-btn period-banner-no" onClick={handlePeriodBannerNotYet} data-testid="period-banner-not-yet">
                Not yet
              </button>
            </div>
            <p className="period-banner-reminder">Log your period when it arrives to keep your calendar accurate</p>
          </div>
        </div>
      )}

      {/* One-time name prompt for existing users */}
      {showNamePrompt && (
        <div className="period-banner" data-testid="name-prompt-banner">
          <div className="period-banner-inner">
            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 16, fontWeight: 700, color: '#1a1a1a', margin: '0 0 4px' }}>What should we call you?</p>
            <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: 12, color: '#999', margin: '0 0 14px' }}>A first name or nickname, whatever you prefer</p>
            <input
              type="text"
              value={namePromptValue}
              onChange={(e) => { setNamePromptValue(e.target.value); setNamePromptError(''); }}
              placeholder="Your name"
              autoFocus
              data-testid="name-prompt-input"
              style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: namePromptError ? '1.5px solid #E53935' : '1.5px solid #e0e0e0', background: '#fafafa', fontSize: 14, fontFamily: "'Nunito', sans-serif", textAlign: 'center', boxSizing: 'border-box', outline: 'none', marginBottom: 4 }}
            />
            {namePromptError && <p style={{ color: '#E53935', fontSize: 12, fontFamily: "'Nunito', sans-serif", margin: '0 0 8px' }}>{namePromptError}</p>}
            <div className="period-banner-actions" style={{ marginTop: 10 }}>
              <button className="period-banner-btn period-banner-yes" onClick={handleNamePromptSave} data-testid="name-prompt-save" style={{ background: '#FF6F00' }}>
                Save
              </button>
              <button className="period-banner-btn period-banner-no" onClick={handleNamePromptSkip} data-testid="name-prompt-skip">
                Skip
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="main-content">
        {activeTab === "cycle" && (
          <YourCycleTab
            currentCycle={currentCycle}
            calendarData={calendarData}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            events={events}
            onAddEvent={handleAddEvent}
            onDeleteEvent={handleDeleteEvent}
            eventCounts={eventCounts}
            onSeasonClick={(phase) => { setSeasonSourceTab("cycle"); setSelectedSeason(phase); setActiveTab("pattern"); }}
          />
        )}
        {activeTab === "pattern" && (
          <YourRhythmTab patternData={patternData} currentCycle={currentCycle} selectedSeason={selectedSeason} setSelectedSeason={setSelectedSeason} onBackFromEducation={() => { setActiveTab(seasonSourceTab); setSeasonSourceTab("pattern"); setSelectedSeason(null); }} />
        )}
        {activeTab === "settings" && (
          <SettingsTab settings={settings} onSave={handleSaveSettings} events={events} onSignOut={handleSignOut} onSharePartner={() => setShowSharePartner(true)} onPreviewIntro={() => setShowPreviewIntro(true)} />
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav" data-testid="bottom-nav">
        <button
          className={`nav-item ${activeTab === "cycle" ? "active" : ""}`}
          onClick={() => setActiveTab("cycle")}
          data-testid="nav-cycle"
        >
          <Calendar size={24} />
          <span>My Calendar</span>
        </button>
        <button
          className={`nav-item ${activeTab === "pattern" ? "active" : ""}`}
          onClick={() => setActiveTab("pattern")}
          data-testid="nav-pattern"
        >
          <Circle size={24} />
          <span>My Cycle</span>
        </button>
        <button
          className={`nav-item ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
          data-testid="nav-settings"
        >
          <Settings size={24} />
          <span>Settings</span>
        </button>
      </nav>

      <Toaster />
    </div>
  );
}

// Payment Success Screen — shown after successful Stripe checkout
const PaymentSuccessScreen = ({ sessionId }) => {
  const [status, setStatus] = useState('loading'); // loading | success | failed | error

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      try {
        const res = await axios.get(`${API}/payment-status/${sessionId}`);
        const { payment_status, status: sessionStatus } = res.data;

        if (payment_status === 'paid') {
          await updateIsPremium();
          setStatus('success');
          setTimeout(() => {
            window.location.replace('/');
          }, 3000);
          return;
        }

        if (sessionStatus === 'expired' || attempts >= maxAttempts) {
          setStatus('failed');
          return;
        }

        attempts++;
        setTimeout(poll, 2000);
      } catch {
        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    poll();
  }, [sessionId]);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0a0a0a',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      fontFamily: "'Helvetica Neue', sans-serif",
      color: '#fff'
    }} data-testid="payment-success-screen">
      <h1 style={{ fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.04em', marginBottom: '0.5rem' }}>ILIK</h1>
      <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.85rem', marginBottom: '3rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>

      {status === 'loading' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.1)',
            borderTop: '3px solid #FF4D6D',
            animation: 'spin 0.9s linear infinite',
            margin: '0 auto 1.5rem'
          }} />
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1rem' }}>Confirming your payment...</p>
        </div>
      )}

      {status === 'success' && (
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF4D6D, #FF8C42)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1.5rem'
          }}>
            <Check size={32} color="#fff" />
          </div>
          <h2 style={{ fontSize: '1.5rem', fontWeight: 700, marginBottom: '0.5rem' }}>Welcome to ILIK+</h2>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95rem', marginBottom: '0.5rem' }}>
            Your subscription is now active.
          </p>
          <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>Taking you back to the app...</p>
        </div>
      )}

      {(status === 'failed' || status === 'error') && (
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '1.5rem', color: 'rgba(255,255,255,0.8)' }}>
            {status === 'failed' ? 'Payment was not completed.' : 'Something went wrong.'}
          </p>
          <button
            onClick={() => window.location.replace('/')}
            style={{
              background: 'linear-gradient(135deg, #FF4D6D, #FF8C42)',
              color: '#fff', border: 'none', borderRadius: '50px',
              padding: '0.85rem 2rem', fontSize: '1rem', fontWeight: 700,
              cursor: 'pointer'
            }}
            data-testid="payment-back-btn"
          >
            Back to ILIK
          </button>
        </div>
      )}
    </div>
  );
};

import LandingPage from "./LandingPage";
import PrivacyPage from "./PrivacyPage";
import TermsPage from "./TermsPage";
import WaitlistPage from "./WaitlistPage";

// Password gate — remove before official launch
const APP_PASSWORD = process.env.REACT_APP_GATE_PASSWORD;
const PasswordGate = ({ onUnlock }) => {
  const [pw, setPw] = useState('');
  const [error, setError] = useState(false);
  const handleSubmit = (e) => {
    e.preventDefault();
    if (pw === APP_PASSWORD) {
      localStorage.setItem('ilik_access', 'granted');
      onUnlock();
    } else {
      setError(true);
      setTimeout(() => setError(false), 1500);
    }
  };
  return (
    <div data-testid="password-gate" style={{ minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff' }}>
      <form onSubmit={handleSubmit} style={{ textAlign: 'center', width: '100%', maxWidth: 340, padding: '0 1.5rem' }}>
        <h1 className="hero-wordmark-gradient" style={{ marginBottom: 24 }}>ILIK</h1>
        <p style={{ fontFamily: "'Poppins', sans-serif", fontSize: '1.1rem', fontWeight: 600, color: '#333', letterSpacing: '0.25em', textTransform: 'uppercase', marginBottom: 6 }}>Coming Soon</p>
        <p style={{ fontFamily: "'Nunito', sans-serif", fontSize: '0.85rem', color: '#999', marginBottom: 36 }}>Enter your access code to preview</p>
        <input
          type="password"
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          placeholder="Access code"
          autoFocus
          data-testid="gate-password-input"
          style={{ width: '100%', padding: '14px 16px', borderRadius: '12px', border: error ? '2px solid #E53935' : '1.5px solid #e0e0e0', background: '#fafafa', color: '#333', fontSize: '0.95rem', fontFamily: "'Nunito', sans-serif", outline: 'none', textAlign: 'center', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
          onFocus={(e) => { if (!error) e.target.style.borderColor = '#FF6F00'; }}
          onBlur={(e) => { if (!error) e.target.style.borderColor = '#e0e0e0'; }}
        />
        <button
          type="submit"
          data-testid="gate-submit-btn"
          style={{ marginTop: 14, width: '100%', padding: '14px', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #FF8F0B, #FF2D78)', color: '#fff', fontWeight: 700, fontSize: '0.95rem', fontFamily: "'Nunito', sans-serif", cursor: 'pointer', letterSpacing: '0.04em', transition: 'opacity 0.15s' }}
          onMouseDown={(e) => { e.target.style.opacity = '0.85'; }}
          onMouseUp={(e) => { e.target.style.opacity = '1'; }}
        >Enter</button>
        {error && <p style={{ color: '#E53935', fontSize: '0.8rem', fontFamily: "'Nunito', sans-serif", marginTop: 12 }}>Incorrect access code</p>}
      </form>
    </div>
  );
};

// Reset Password Screen — shown when arriving via Supabase recovery link
const ResetPasswordScreen = ({ onComplete }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const canSubmit = password.length >= 6 && passwordsMatch && !isSubmitting;

  const handleSubmit = async () => {
    if (password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (password !== confirmPassword) { setError("Passwords don't match"); return; }

    setIsSubmitting(true);
    setError('');

    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message || 'Failed to update password. The link may have expired.');
        setIsSubmitting(false);
        return;
      }
      toast.success('Password updated!');
      onComplete();
    } catch {
      setError('Something went wrong. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="onboarding-setup-screen fade-in" data-testid="reset-password-screen">
      <div className="setup-content account-creation">
        <h1 className="account-wordmark-gradient">ILIK</h1>
        <p className="app-tagline" style={{ textAlign: 'center', marginTop: '0', marginBottom: '1.5rem' }}>4 Seasons of You<sup style={{fontSize: '0.5em'}}>®</sup></p>
        <h2 className="setup-heading">Set new password</h2>

        <div className="account-form">
          <div className="account-input-wrapper">
            <input
              type="password"
              placeholder="New password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(''); }}
              className="account-input account-input-icon"
              data-testid="reset-new-password-input"
            />
            {password.length >= 6 && <span className="input-valid-check"><Check size={15} /></span>}
          </div>
          <div className="account-input-wrapper">
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
              className="account-input account-input-icon"
              data-testid="reset-confirm-password-input"
            />
            {confirmPassword && passwordsMatch && password.length >= 6 && <span className="input-valid-check"><Check size={15} /></span>}
            {confirmPassword && !passwordsMatch && !error && (
              <p className="account-error">Passwords don't match</p>
            )}
          </div>
          {error && <p className="account-error" style={{ textAlign: 'center' }}>{error}</p>}
        </div>

        <button
          className="hero-pill-btn orange"
          onClick={handleSubmit}
          disabled={!canSubmit}
          style={{ opacity: canSubmit ? 1 : 0.5 }}
          data-testid="reset-password-submit-btn"
        >
          {isSubmitting ? 'Updating…' : 'Set new password'}
        </button>
      </div>
      <Toaster />
    </div>
  );
};

// Root component
function App() {
  const [gateUnlocked, setGateUnlocked] = useState(() => localStorage.getItem('ilik_access') === 'granted');
  const [showResetPassword, setShowResetPassword] = useState(() => window.location.hash.includes('type=recovery'));
  const path = window.location.pathname;

  // Detect Capacitor native app — add class for safe area spacer
  useEffect(() => {
    if (window.Capacitor?.isNativePlatform?.()) {
      document.body.classList.add('capacitor-native');
    }
  }, []);

  // Set Supabase session from recovery hash tokens on mount
  useEffect(() => {
    if (!showResetPassword || !supabase) return;
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    if (accessToken) {
      supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken || '' })
        .catch(err => console.error('Recovery session error:', err));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Password recovery screen (takes priority over all routes)
  if (showResetPassword) {
    return (
      <ResetPasswordScreen onComplete={() => {
        window.history.replaceState(null, '', '/app');
        window.location.hash = '';
        setShowResetPassword(false);
        window.location.href = '/app';
      }} />
    );
  }

  // Public landing page at /
  if (path === '/' || path === '') return <LandingPage />;

  // Public legal pages
  if (path === '/privacy') return <PrivacyPage />;
  if (path === '/terms') return <TermsPage />;
  if (path === '/waitlist') return <WaitlistPage />;

  // Partner view
  const partnerMatch = path.match(/^\/partner\/([^/]+)$/);
  if (partnerMatch) return <PartnerView token={partnerMatch[1]} />;

  // Payment success
  if (path === '/payment-success') {
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (sessionId) return <PaymentSuccessScreen sessionId={sessionId} />;
  }

  // Everything else (/app and any sub-paths) — password gate → app
  if (!gateUnlocked) return <PasswordGate onUnlock={() => setGateUnlocked(true)} />;
  return <MainApp />;
}

export default App;
